<template>
  <div class="user-list-page">
    <section class="user-list-main page-card">
      <div class="page-card__head">
        <div>
          <div class="page-card__title">用户管理</div>
          <div class="page-card__desc">查看小程序用户，支持编辑资料、停用账号、删除与恢复，方便运营人员处理用户问题。</div>
        </div>
        <div class="user-list-head-actions">
          <button class="ghost-button" type="button" :disabled="loading" @click="loadUsers">
            {{ loading ? "刷新中..." : "刷新" }}
          </button>
          <button class="action-button" type="button" :disabled="exporting" @click="exportUsers">
            {{ exporting ? "导出中..." : "导出数据" }}
          </button>
        </div>
      </div>

      <div class="user-list-toolbar">
        <div class="user-list-search">
          <input
            v-model.trim="keyword"
            type="text"
            placeholder="搜索昵称 / OpenID / 手机号 / 用户ID"
            @input="handleKeywordChange"
          />
        </div>

        <div class="user-list-filters">
          <label class="user-list-filter">
            <span>账号状态</span>
            <select v-model="statusFilter" @change="handleFilterChange">
              <option value="">全部</option>
              <option value="1">正常</option>
              <option value="0">已停用</option>
            </select>
          </label>

          <label class="user-list-filter user-list-filter--switch">
            <input v-model="showDeleted" type="checkbox" @change="handleFilterChange" />
            <span>包含已删除</span>
          </label>
        </div>

        <div class="user-list-toolbar__meta">
          <span>共 {{ total }} 个用户</span>
          <label class="user-list-page-size">
            <span>每页</span>
            <select v-model.number="pageSize" @change="handlePageSizeChange">
              <option :value="10">10</option>
              <option :value="20">20</option>
              <option :value="50">50</option>
            </select>
          </label>
        </div>
      </div>

      <div v-if="loading" class="page-state user-list-state">加载中...</div>

      <div v-else class="user-list-table-wrap">
        <table class="table user-list-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>用户</th>
              <th>OpenID</th>
              <th>手机号</th>
              <th>宠物数量</th>
              <th>状态</th>
              <th>创建时间</th>
              <th class="sticky-action-column">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in users" :key="item.id">
              <td>{{ item.id }}</td>
              <td>
                <div class="user-cell">
                  <div class="user-avatar">
                    <img
                      v-if="shouldShowAvatar(item)"
                      :src="resolveAvatarUrl(item.avatarUrl)"
                      :alt="displayUserName(item)"
                      @error="handleAvatarError(item)"
                    />
                    <span v-else>{{ avatarInitial(item) }}</span>
                  </div>
                  <div class="user-cell__copy">
                    <div class="user-cell__name">{{ displayUserName(item) }}</div>
                    <div class="user-cell__id">
                      UID {{ item.id }}
                      <span v-if="item.official === 1" class="user-cell__badge">官方号</span>
                    </div>
                  </div>
                </div>
              </td>
              <td>
                <div class="user-openid-cell">
                  <span class="user-list-openid">{{ item.openId || "-" }}</span>
                  <button
                    class="mini-button"
                    type="button"
                    :disabled="!item.openId"
                    @click="copyOpenId(item)"
                  >
                    {{ copiedUserId === item.id ? "已复制" : "复制" }}
                  </button>
                </div>
              </td>
              <td>{{ item.phone || "-" }}</td>
              <td>{{ item.petCount || 0 }}</td>
              <td>
                <span class="user-status-tag" :class="statusClass(item)">{{ statusLabel(item) }}</span>
              </td>
              <td>{{ formatCreatedAt(item.createdAt) }}</td>
              <td class="sticky-action-column">
                <div class="user-list-actions">
                  <template v-if="isDeletedUser(item)">
                    <button
                      class="table-action-button"
                      type="button"
                      :disabled="!canDeleteUser"
                      @click="restoreUser(item)"
                    >
                      恢复
                    </button>
                    <button
                      class="table-action-button table-action-button--danger"
                      type="button"
                      :disabled="!canDeleteUser"
                      @click="openClearData(item)"
                    >
                      清空数据
                    </button>
                  </template>
                  <template v-else>
                    <button
                      class="table-action-button"
                      type="button"
                      :disabled="!canEditUser"
                      @click="openUserEditor(item)"
                    >
                      编辑
                    </button>
                    <button
                      class="table-action-button table-action-button--ghost"
                      type="button"
                      :disabled="isPetModalLoadingFor(item)"
                      @click="openPetModal(item)"
                    >
                      {{ isPetModalLoadingFor(item) ? "加载中" : "宠物" }}
                    </button>
                    <button
                      class="table-action-button table-action-button--ghost"
                      type="button"
                      @click="openMinePreview(item)"
                    >
                      我的界面
                    </button>
                    <button
                      class="table-action-button table-action-button--ghost"
                      type="button"
                      @click="openPetCreate(item)"
                    >
                      加宠物
                    </button>
                    <button
                      class="table-action-button table-action-button--ghost"
                      type="button"
                      :disabled="!canEditUser"
                      @click="toggleUserStatus(item)"
                    >
                      {{ item.status === 1 ? "停用" : "启用" }}
                    </button>
                    <button
                      class="table-action-button table-action-button--danger"
                      type="button"
                      :disabled="!canDeleteUser"
                      @click="openDelete(item)"
                    >
                      删除
                    </button>
                  </template>
                </div>
              </td>
            </tr>
            <tr v-if="users.length === 0">
              <td colspan="8">{{ emptyStateText }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="user-list-footer">
        <div class="user-list-footer__meta">
          共 {{ total }} 条
          <span v-if="total > 0">，当前 {{ pageStart }} - {{ pageEnd }}</span>
        </div>

        <div class="user-list-pagination">
          <button class="ghost-button" :disabled="currentPage <= 1" @click="goToPage(currentPage - 1)">上一页</button>

          <button
            v-for="page in visiblePages"
            :key="page"
            class="user-list-page-button"
            :class="{ 'user-list-page-button--active': currentPage === page }"
            @click="goToPage(page)"
          >
            {{ page }}
          </button>

          <button class="ghost-button" :disabled="currentPage >= totalPages" @click="goToPage(currentPage + 1)">下一页</button>
        </div>
      </div>
    </section>

    <div v-if="petModalVisible" class="user-pet-modal" @click.self="closePetModal">
      <div class="user-pet-modal__panel" role="dialog" aria-modal="true">
        <div class="user-pet-modal__head">
          <div>
            <div class="page-card__title">名下宠物</div>
            <div class="page-card__desc">
              {{ petModalUser ? `${displayUserName(petModalUser)} 的宠物资料` : "用户宠物资料" }}
            </div>
          </div>
          <button class="modal-close-button" type="button" aria-label="关闭" @click="closePetModal">×</button>
        </div>

        <div v-if="petModalLoading" class="page-state user-pet-modal__state">宠物加载中...</div>
        <div v-else-if="petModalError" class="page-state user-pet-modal__state">{{ petModalError }}</div>

        <div v-else class="user-pet-modal__table-wrap">
          <table class="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>名字</th>
                <th>品类</th>
                <th>阶段</th>
                <th>标签</th>
                <th class="sticky-action-column">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="pet in petModalPets" :key="pet.id">
                <td>{{ pet.id }}</td>
                <td>{{ pet.name || "-" }}</td>
                <td>{{ pet.species || "-" }}</td>
                <td>{{ pet.stage || "-" }}</td>
                <td>{{ pet.careTags || "-" }}</td>
                <td class="sticky-action-column">
                  <div class="pet-row-actions">
                    <button class="table-action-button" type="button" @click="openPetDetail(pet, 'profile')">资料</button>
                    <button
                      class="table-action-button table-action-button--ghost"
                      type="button"
                      :disabled="!canLoadPetData"
                      @click="openPetDetail(pet, 'care')"
                    >
                      照护
                    </button>
                    <button
                      class="table-action-button table-action-button--ghost"
                      type="button"
                      :disabled="!canLoadPetData"
                      @click="openPetDetail(pet, 'weight')"
                    >
                      体重
                    </button>
                    <button
                      class="table-action-button table-action-button--ghost"
                      type="button"
                      :disabled="!canLoadPetData"
                      @click="openPetDetail(pet, 'cost')"
                    >
                      成本
                    </button>
                    <button
                      class="table-action-button table-action-button--ghost"
                      type="button"
                      :disabled="!canLoadPetData"
                      @click="openPetDetail(pet, 'plan')"
                    >
                      计划
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="petModalPets.length === 0">
                <td colspan="6">该用户还没有录入宠物</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="user-pet-modal__foot">
          <span>共 {{ petModalPets.length }} 只宠物</span>
          <div class="user-pet-modal__foot-actions">
            <button class="table-action-button" type="button" @click="openPetCreate(petModalUser)">添加宠物</button>
            <button class="ghost-button" type="button" @click="closePetModal">关闭</button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="petDetailVisible" class="pet-detail-modal" @click.self="closePetDetail">
      <div class="pet-detail-modal__panel" role="dialog" aria-modal="true">
        <div class="user-pet-modal__head">
          <div>
            <div class="page-card__title">{{ petDetailTitle }}</div>
            <div class="page-card__desc">{{ activePet ? `${activePet.name || "未命名宠物"} · ${activePet.species || "未填写品类"}` : "宠物资料" }}</div>
          </div>
          <button class="modal-close-button" type="button" aria-label="关闭" @click="closePetDetail">×</button>
        </div>

        <div v-if="petDetailLoading" class="page-state user-pet-modal__state">数据加载中...</div>
        <div v-else-if="petDetailError" class="page-state user-pet-modal__state">{{ petDetailError }}</div>

        <div v-else class="pet-detail-body">
          <div v-if="petDetailType === 'profile'" class="pet-profile-detail">
            <div class="pet-cover-preview">
              <img v-if="activePet && activePet.coverUrl" :src="resolveFileUrl(activePet.coverUrl)" :alt="`${activePet.name || '宠物'}背景图`" />
              <span v-else>暂无背景图</span>
            </div>
            <div class="pet-profile-card">
              <div class="pet-avatar-preview">
                <img v-if="activePet && activePet.avatarUrl" :src="resolveFileUrl(activePet.avatarUrl)" :alt="`${activePet.name || '宠物'}头像`" />
                <span v-else>{{ petInitial(activePet) }}</span>
              </div>
              <div class="pet-profile-card__copy">
                <div class="pet-profile-card__name">{{ activePet.name || "未命名宠物" }}</div>
                <div class="pet-profile-card__meta">{{ activePet.species || "-" }} / {{ activePet.morph || "未填基因" }}</div>
                <div class="pet-profile-card__intro">{{ activePet.intro || "暂无简介" }}</div>
              </div>
            </div>
            <div class="pet-detail-grid">
              <div v-for="item in petProfileFields" :key="item.label" class="pet-detail-field">
                <span>{{ item.label }}</span>
                <strong>{{ item.value }}</strong>
              </div>
            </div>
          </div>

          <div v-else-if="petDetailType === 'care'" class="pet-detail-section">
            <table class="table pet-detail-table">
              <thead>
                <tr>
                  <th>类型</th>
                  <th>标题</th>
                  <th>内容</th>
                  <th>关键数据</th>
                  <th>时间</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="record in petDetailData.careRecords" :key="record.id">
                  <td>{{ recordTypeLabel(record.recordType) }}</td>
                  <td>{{ record.title || "-" }}</td>
                  <td>{{ record.content || "-" }}</td>
                  <td>{{ careRecordSummary(record) }}</td>
                  <td>{{ formatDate(record.occurredAt) }}</td>
                </tr>
                <tr v-if="petDetailData.careRecords.length === 0">
                  <td colspan="5">暂无照护记录</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else-if="petDetailType === 'weight'" class="pet-detail-section">
            <table class="table pet-detail-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>体重</th>
                  <th>来源</th>
                  <th>备注</th>
                  <th>时间</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="record in petDetailData.weightRecords" :key="record.id">
                  <td>{{ record.id }}</td>
                  <td>{{ formatWeight(record.weightValue) }}</td>
                  <td>{{ record.sourceType || "-" }}</td>
                  <td>{{ record.note || "-" }}</td>
                  <td>{{ formatDate(record.measuredAt) }}</td>
                </tr>
                <tr v-if="petDetailData.weightRecords.length === 0">
                  <td colspan="5">暂无体重记录</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else-if="petDetailType === 'cost'" class="pet-detail-section">
            <div class="pet-cost-summary">
              <div class="pet-detail-field">
                <span>累计花费</span>
                <strong>{{ formatMoney(petDetailData.costSummary.totalAmount) }}</strong>
              </div>
              <div class="pet-detail-field">
                <span>本月花费</span>
                <strong>{{ formatMoney(petDetailData.costSummary.latestMonthAmount) }}</strong>
              </div>
              <div class="pet-detail-field">
                <span>记录数</span>
                <strong>{{ petDetailData.costSummary.totalRecordCount || 0 }}</strong>
              </div>
            </div>
            <table class="table pet-detail-table">
              <thead>
                <tr>
                  <th>分类</th>
                  <th>项目</th>
                  <th>金额</th>
                  <th>备注</th>
                  <th>时间</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="record in petDetailData.costRecords" :key="record.id">
                  <td>{{ record.categoryLabel || record.categoryCode || "-" }}</td>
                  <td>{{ record.itemName || "-" }}</td>
                  <td>{{ formatMoney(record.amount) }}</td>
                  <td>{{ record.note || "-" }}</td>
                  <td>{{ formatDate(record.occurredAt) }}</td>
                </tr>
                <tr v-if="petDetailData.costRecords.length === 0">
                  <td colspan="5">暂无成本记录</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else-if="petDetailType === 'plan'" class="pet-detail-section">
            <div class="pet-detail-grid">
              <div class="pet-detail-field">
                <span>喂食记录</span>
                <strong>{{ petDetailData.feedingPlan.feedingRecordCount || 0 }}</strong>
              </div>
              <div class="pet-detail-field">
                <span>体重记录</span>
                <strong>{{ petDetailData.feedingPlan.weightRecordCount || 0 }}</strong>
              </div>
              <div class="pet-detail-field">
                <span>健康记录</span>
                <strong>{{ petDetailData.feedingPlan.healthRecordCount || 0 }}</strong>
              </div>
              <div class="pet-detail-field">
                <span>当前体重</span>
                <strong>{{ petDetailData.feedingPlan.currentWeightText || "-" }}</strong>
              </div>
            </div>
            <div class="pet-plan-block">
              <h4>手动计划</h4>
              <p>{{ feedingManualPlanText }}</p>
            </div>
            <div class="pet-plan-block">
              <h4>AI 计划</h4>
              <p>{{ petDetailData.feedingPlan.aiPlanSummary || petDetailData.feedingPlan.aiPlanDetails || "暂无 AI 计划" }}</p>
            </div>
          </div>
        </div>

        <div class="user-pet-modal__foot">
          <span>{{ petDetailFooter }}</span>
          <button class="ghost-button" type="button" @click="closePetDetail">关闭</button>
        </div>
      </div>
    </div>

    <div v-if="petCreateVisible" class="pet-create-modal" @click.self="closePetCreate">
      <div class="pet-create-modal__panel" role="dialog" aria-modal="true">
        <div class="user-pet-modal__head">
          <div>
            <div class="page-card__title">添加宠物</div>
            <div class="page-card__desc">
              {{
                petCreateUser
                  ? `为 ${displayUserName(petCreateUser)} 新建宠物档案：可以手动填写，也可以上传图片让 AI 识别后逐条确认。`
                  : "新建宠物档案"
              }}
            </div>
          </div>
          <button class="modal-close-button" type="button" aria-label="关闭" @click="closePetCreate">×</button>
        </div>

        <div class="pet-create-tabs">
          <button
            class="pet-create-tab"
            :class="{ 'pet-create-tab--active': petCreateTab === 'form' }"
            type="button"
            @click="switchPetCreateTab('form')"
          >
            手动填写
          </button>
          <button
            class="pet-create-tab"
            :class="{ 'pet-create-tab--active': petCreateTab === 'image' }"
            type="button"
            @click="switchPetCreateTab('image')"
          >
            上传图片 AI 识别
          </button>
        </div>

        <div class="pet-create-body">
          <div v-if="petCreateNotice" class="pet-create-notice">{{ petCreateNotice }}</div>

          <div v-if="petCreateTab === 'form'" class="pet-create-section">
            <div class="pet-form-grid">
              <label class="pet-form-field">
                <span>宠物名字</span>
                <input v-model.trim="petForm.name" type="text" placeholder="比如 奶盖" maxlength="32" />
              </label>

              <label class="pet-form-field">
                <span>大类</span>
                <select v-model="petForm.speciesGroupCode" @change="handlePetFormGroupChange">
                  <option value="">未选择</option>
                  <option v-for="item in petTypeLevel1Options" :key="item.typeCode" :value="item.typeCode">
                    {{ item.typeName }}
                  </option>
                </select>
              </label>

              <label class="pet-form-field">
                <span>品类</span>
                <select v-model="petForm.speciesCode" :disabled="!petForm.speciesGroupCode" @change="handlePetFormSpeciesChange">
                  <option value="">未选择</option>
                  <option v-for="item in petTypeLevel2Options" :key="item.typeCode" :value="item.typeCode">
                    {{ item.typeName }}
                  </option>
                </select>
              </label>

              <label class="pet-form-field">
                <span>三级分类</span>
                <select v-model="petForm.speciesDetailCode" :disabled="!petForm.speciesCode">
                  <option value="">未选择</option>
                  <option v-for="item in petTypeLevel3Options" :key="item.typeCode" :value="item.typeCode">
                    {{ item.typeName }}
                  </option>
                </select>
              </label>

              <label class="pet-form-field">
                <span>品类名称</span>
                <input v-model.trim="petForm.species" type="text" placeholder="选择品类后自动填写，也可以手写" maxlength="64" />
              </label>

              <label class="pet-form-field">
                <span>基因 / 品系</span>
                <input v-model.trim="petForm.morph" type="text" placeholder="比如 白化" maxlength="64" />
              </label>

              <label class="pet-form-field">
                <span>性别</span>
                <select v-model="petForm.sex">
                  <option value="">未选择</option>
                  <option value="公">公</option>
                  <option value="母">母</option>
                  <option value="未知">未知</option>
                </select>
              </label>

              <label class="pet-form-field">
                <span>阶段</span>
                <select v-model="petForm.stage">
                  <option value="">未选择</option>
                  <option value="苗子">苗子</option>
                  <option value="幼体">幼体</option>
                  <option value="亚成体">亚成体</option>
                  <option value="成体">成体</option>
                </select>
              </label>

              <label class="pet-form-field">
                <span>当前体重（克）</span>
                <input v-model.trim="petForm.currentWeight" type="text" placeholder="比如 18.5" maxlength="12" />
              </label>

              <label class="pet-form-field">
                <span>到家体重（克）</span>
                <input v-model.trim="petForm.arrivalWeight" type="text" placeholder="可留空" maxlength="12" />
              </label>

              <label class="pet-form-field">
                <span>体长（厘米）</span>
                <input v-model.trim="petForm.bodyLengthCm" type="text" placeholder="可留空" maxlength="12" />
              </label>

              <label class="pet-form-field">
                <span>孵化日期</span>
                <input v-model="petForm.hatchDate" type="date" />
              </label>

              <label class="pet-form-field">
                <span>到家日期</span>
                <input v-model="petForm.adoptDate" type="date" />
              </label>

              <label class="pet-form-field">
                <span>照顾标签</span>
                <input v-model.trim="petForm.careTags" type="text" placeholder="多个用逗号分隔，比如 夜行,进食稳定" maxlength="120" />
              </label>
            </div>

            <label class="pet-form-field pet-form-field--full">
              <span>简介</span>
              <textarea v-model.trim="petForm.intro" rows="3" placeholder="外观、性格、来源等补充说明，可留空" maxlength="500"></textarea>
            </label>

            <div class="pet-form-media-grid">
              <div class="pet-form-media">
                <div class="pet-form-media__preview pet-form-media__preview--avatar">
                  <img v-if="petForm.avatarUrl" :src="resolveFileUrl(petForm.avatarUrl)" alt="宠物头像预览" />
                  <span v-else>暂无头像</span>
                </div>
                <div class="pet-form-media__copy">
                  <div class="pet-form-media__title">宠物头像</div>
                  <div class="pet-form-media__actions">
                    <button
                      class="ghost-button"
                      type="button"
                      :disabled="petFormUploading.avatarUrl"
                      @click="triggerMediaPick('petAvatarInput')"
                    >
                      {{ petFormUploading.avatarUrl ? "上传中..." : petForm.avatarUrl ? "重新选择" : "上传头像" }}
                    </button>
                    <button v-if="petForm.avatarUrl" class="ghost-button" type="button" @click="setPetFormMedia('avatarUrl', '')">移除</button>
                  </div>
                  <input
                    ref="petAvatarInput"
                    class="pet-form-media__input"
                    type="file"
                    accept="image/*"
                    @change="handlePetFormMediaPick($event, 'avatarUrl')"
                  />
                </div>
              </div>

              <div class="pet-form-media">
                <div class="pet-form-media__preview">
                  <img v-if="petForm.coverUrl" :src="resolveFileUrl(petForm.coverUrl)" alt="宠物背景图预览" />
                  <span v-else>暂无背景图</span>
                </div>
                <div class="pet-form-media__copy">
                  <div class="pet-form-media__title">宠物背景图</div>
                  <div class="pet-form-media__actions">
                    <button
                      class="ghost-button"
                      type="button"
                      :disabled="petFormUploading.coverUrl"
                      @click="triggerMediaPick('petCoverInput')"
                    >
                      {{ petFormUploading.coverUrl ? "上传中..." : petForm.coverUrl ? "重新选择" : "上传背景图" }}
                    </button>
                    <button v-if="petForm.coverUrl" class="ghost-button" type="button" @click="setPetFormMedia('coverUrl', '')">移除</button>
                  </div>
                  <input
                    ref="petCoverInput"
                    class="pet-form-media__input"
                    type="file"
                    accept="image/*"
                    @change="handlePetFormMediaPick($event, 'coverUrl')"
                  />
                </div>
              </div>
            </div>
          </div>

          <div v-else class="pet-create-section">
            <div class="pet-ai-upload">
              <div class="pet-ai-dropzone" :class="{ 'pet-ai-dropzone--filled': Boolean(aiImagePreviewUrl) }">
                <img v-if="aiImagePreviewUrl" :src="aiImagePreviewUrl" alt="待识别图片" />
                <div v-else class="pet-ai-dropzone__hint">
                  <strong>上传宠物照片或宠物清单截图</strong>
                  <span>单只宠物照片会识别品种、性别、阶段等；清单、表格或聊天记录截图会按条列出候选项。识别结果需要你确认后才会保存。</span>
                </div>
                <input ref="petAiImageInput" class="pet-form-media__input" type="file" accept="image/*" @change="handleAiImagePick" />
              </div>

              <div class="pet-ai-upload__actions">
                <button class="ghost-button" type="button" @click="triggerMediaPick('petAiImageInput')">
                  {{ aiImageFile ? "重新选择图片" : "选择图片" }}
                </button>
                <button class="table-action-button" type="button" :disabled="!aiImageFile || aiParsing" @click="startAiParse">
                  {{ aiParsing ? "AI 识别中..." : "开始识别" }}
                </button>
                <button v-if="aiImageFile || aiDrafts.length > 0" class="ghost-button" type="button" @click="resetAiParse">清空</button>
              </div>
            </div>

            <div v-if="aiParseError" class="pet-ai-message pet-ai-message--error">{{ aiParseError }}</div>
            <div v-else-if="aiSummary" class="pet-ai-message">{{ aiSummary }}</div>
            <div v-if="aiParsed && aiDrafts.length === 0 && !aiParseError" class="page-state pet-ai-empty">
              这张图片里没有识别到可录入的宠物，可以换一张更清晰的图片再试。
            </div>

            <div v-if="aiDrafts.length > 0" class="pet-ai-toolbar">
              <label class="pet-ai-toolbar__check">
                <input type="checkbox" :checked="allAiDraftsChecked" @change="toggleAllAiDrafts($event.target.checked)" />
                <span>全选</span>
              </label>
              <label class="pet-ai-toolbar__check">
                <input v-model="aiUseImageAsMedia" type="checkbox" />
                <span>把这张图片作为选中宠物的头像和背景图</span>
              </label>
              <span class="pet-ai-toolbar__count">共 {{ aiDrafts.length }} 条，已选 {{ checkedAiDraftCount }} 条</span>
            </div>

            <div v-if="aiDrafts.length > 0" class="pet-ai-list">
              <div v-for="(draft, index) in aiDrafts" :key="draft._key" class="pet-ai-item">
                <div class="pet-ai-item__head">
                  <label class="pet-ai-item__check">
                    <input v-model="draft.checked" type="checkbox" />
                    <span class="pet-ai-item__title">{{ draft.name || draft.species || `候选 ${index + 1}` }}</span>
                  </label>
                  <span class="pet-ai-item__badge" :class="`pet-ai-item__badge--${draft.confidence || 'low'}`">
                    {{ confidenceLabel(draft.confidence) }}
                  </span>
                </div>

                <div class="pet-ai-item__grid">
                  <label class="pet-form-field">
                    <span>名字</span>
                    <input v-model.trim="draft.name" type="text" maxlength="32" placeholder="未命名宠物" />
                  </label>
                  <label class="pet-form-field">
                    <span>大类</span>
                    <select v-model="draft.speciesGroupCode" @change="handleAiDraftGroupChange(draft)">
                      <option value="">未选择</option>
                      <option v-for="item in petTypeLevel1Options" :key="item.typeCode" :value="item.typeCode">
                        {{ item.typeName }}
                      </option>
                    </select>
                  </label>
                  <label class="pet-form-field">
                    <span>品类</span>
                    <select v-model="draft.speciesCode" :disabled="!draft.speciesGroupCode" @change="handleAiDraftSpeciesChange(draft)">
                      <option value="">未选择</option>
                      <option v-for="item in petTypeLevel2OptionsFor(draft.speciesGroupCode)" :key="item.typeCode" :value="item.typeCode">
                        {{ item.typeName }}
                      </option>
                    </select>
                  </label>
                  <label class="pet-form-field">
                    <span>品类名称</span>
                    <input v-model.trim="draft.species" type="text" maxlength="64" placeholder="可手写补充" />
                  </label>
                  <label class="pet-form-field">
                    <span>基因 / 品系</span>
                    <input v-model.trim="draft.morph" type="text" maxlength="64" placeholder="可留空" />
                  </label>
                  <label class="pet-form-field">
                    <span>性别</span>
                    <select v-model="draft.sex">
                      <option value="">未选择</option>
                      <option value="公">公</option>
                      <option value="母">母</option>
                      <option value="未知">未知</option>
                    </select>
                  </label>
                  <label class="pet-form-field">
                    <span>阶段</span>
                    <select v-model="draft.stage">
                      <option value="">未选择</option>
                      <option value="苗子">苗子</option>
                      <option value="幼体">幼体</option>
                      <option value="亚成体">亚成体</option>
                      <option value="成体">成体</option>
                    </select>
                  </label>
                  <label class="pet-form-field">
                    <span>当前体重（克）</span>
                    <input v-model.trim="draft.currentWeight" type="text" maxlength="12" placeholder="可留空" />
                  </label>
                  <label class="pet-form-field">
                    <span>体长（厘米）</span>
                    <input v-model.trim="draft.bodyLengthCm" type="text" maxlength="12" placeholder="可留空" />
                  </label>
                  <label class="pet-form-field">
                    <span>孵化日期</span>
                    <input v-model="draft.hatchDate" type="date" />
                  </label>
                  <label class="pet-form-field">
                    <span>到家日期</span>
                    <input v-model="draft.adoptDate" type="date" />
                  </label>
                  <label class="pet-form-field">
                    <span>照顾标签</span>
                    <input v-model.trim="draft.careTags" type="text" maxlength="120" placeholder="多个用逗号分隔" />
                  </label>
                </div>

                <label class="pet-form-field pet-form-field--full">
                  <span>简介</span>
                  <textarea v-model.trim="draft.intro" rows="2" maxlength="500" placeholder="可留空"></textarea>
                </label>

                <div v-if="draft.note" class="pet-ai-item__note">AI 备注：{{ draft.note }}</div>

                <div class="pet-ai-item__actions">
                  <span class="pet-ai-item__state">{{ draft.saving ? "添加中..." : draft.saved ? "已添加" : "" }}</span>
                  <button
                    class="ghost-button"
                    type="button"
                    :disabled="draft.saving || draft.saved"
                    @click="createAiDraft(draft)"
                  >
                    只添加这一条
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="pet-create-foot">
          <span class="pet-create-foot__hint">{{ petCreateHint }}</span>
          <div class="pet-create-foot__actions">
            <button class="ghost-button" type="button" @click="closePetCreate">关闭</button>
            <button
              v-if="petCreateTab === 'form'"
              class="table-action-button"
              type="button"
              :disabled="petFormSubmitting"
              @click="submitPetForm"
            >
              {{ petFormSubmitting ? "保存中..." : "保存宠物" }}
            </button>
            <button
              v-else
              class="table-action-button"
              type="button"
              :disabled="checkedAiDraftCount === 0 || aiSubmitting"
              @click="createCheckedAiDrafts"
            >
              {{ aiSubmitting ? "添加中..." : `确认添加（${checkedAiDraftCount}）` }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="userEditorVisible" class="pet-create-modal" @click.self="closeUserEditor">
      <form class="user-editor-panel" @submit.prevent="submitUserEditor">
        <div class="user-pet-modal__head">
          <div>
            <div class="page-card__title">编辑用户</div>
            <div class="page-card__desc">
              UID {{ userForm.id }} · {{ displayUserName(userForm) }}
            </div>
          </div>
          <button class="modal-close-button" type="button" aria-label="关闭" @click="closeUserEditor">×</button>
        </div>

        <div class="user-editor-body">
          <div class="user-editor-avatar">
            <div class="user-editor-avatar__preview">
              <img v-if="userForm.avatarUrl" :src="resolveAvatarUrl(userForm.avatarUrl)" alt="用户头像预览" />
              <span v-else>{{ avatarInitial(userForm) }}</span>
            </div>
            <div class="user-editor-avatar__body">
              <label class="user-avatar-upload-button">
                {{ userAvatarUploading ? "上传中…" : "上传头像图片" }}
                <input type="file" accept="image/*" :disabled="userAvatarUploading" @change="pickUserAvatar($event)" />
              </label>
              <input
                v-model.trim="userForm.avatarUrl"
                class="user-avatar-input"
                type="text"
                maxlength="255"
                placeholder="或直接贴图片地址（二选一）"
              />
              <div class="user-editor-avatar__hint">后台直接设置的头像会立即生效，不再走微信头像审核。</div>
            </div>
          </div>

          <div class="user-editor-grid">
            <label class="pet-form-field">
              <span>昵称</span>
              <input v-model.trim="userForm.nickname" type="text" maxlength="64" required placeholder="用户在小程序里显示的昵称" />
            </label>
            <label class="pet-form-field">
              <span>手机号</span>
              <input v-model.trim="userForm.phone" type="text" maxlength="32" placeholder="可以留空" />
            </label>
            <label class="pet-form-field">
              <span>账号状态</span>
              <select v-model.number="userForm.status">
                <option :value="1">正常</option>
                <option :value="0">停用（无法再登录小程序）</option>
              </select>
            </label>
          </div>

          <div class="user-editor-meta">
            <span>OpenID {{ userForm.openId || "-" }}</span>
            <span>宠物 {{ userForm.petCount || 0 }} 只</span>
            <span>微信绑定 {{ userForm.wechatBound ? "已绑定" : "未绑定" }}</span>
            <span>服务号 {{ userForm.officialBound ? "已关注" : "未关注" }}</span>
            <span>注册时间 {{ formatCreatedAt(userForm.createdAt) }}</span>
          </div>

          <div v-if="userEditorError" class="pet-ai-message pet-ai-message--error">{{ userEditorError }}</div>
        </div>

        <div class="pet-create-foot">
          <span class="pet-create-foot__hint">停用后该用户无法再登录小程序，重新启用即可恢复。</span>
          <div class="pet-create-foot__actions">
            <button class="ghost-button" type="button" @click="closeUserEditor">取消</button>
            <button class="action-button" type="submit" :disabled="userSaving">
              {{ userSaving ? "保存中..." : "保存资料" }}
            </button>
          </div>
        </div>
      </form>
    </div>

    <div v-if="confirmVisible" class="pet-create-modal" @click.self="closeConfirm">
      <div class="user-confirm-panel">
        <div class="user-pet-modal__head">
          <div>
            <div class="page-card__title">{{ confirmTitle }}</div>
            <div class="page-card__desc">{{ confirmDesc }}</div>
          </div>
          <button class="modal-close-button" type="button" aria-label="关闭" @click="closeConfirm">×</button>
        </div>

        <div class="user-confirm-body">
          <ul class="user-confirm-list">
            <li v-for="line in confirmNotes" :key="line">{{ line }}</li>
          </ul>
          <label class="user-confirm-check">
            <input v-model="confirmChecked" type="checkbox" />
            <span>{{ confirmCheckLabel }}</span>
          </label>
          <div v-if="confirmError" class="pet-ai-message pet-ai-message--error">{{ confirmError }}</div>
        </div>

        <div class="pet-create-foot">
          <span class="pet-create-foot__hint">{{ confirmTargetText }}</span>
          <div class="pet-create-foot__actions">
            <button class="ghost-button" type="button" @click="closeConfirm">取消</button>
            <button
              class="danger-action-button"
              type="button"
              :disabled="!confirmChecked || confirmSaving"
              @click="runConfirmAction"
            >
              {{ confirmSaving ? "处理中..." : confirmActionLabel }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="toast" class="user-toast" :class="{ 'user-toast--error': toastError }">{{ toast }}</div>

    <div v-if="minePreviewVisible" class="mine-preview-modal" @click.self="closeMinePreview">
      <div class="mine-preview-panel">
        <div class="user-pet-modal__head">
          <div>
            <div class="page-card__title">用户「我的」界面</div>
            <div class="page-card__desc">
              {{ minePreviewLoading ? "正在读取该用户的移动端数据…" : `还原小程序「我的」页面的样子，只读预览 · 数据来自该用户真实资料` }}
            </div>
          </div>
          <button class="modal-close-button" type="button" aria-label="关闭" @click="closeMinePreview">×</button>
        </div>

        <div v-if="minePreviewLoading" class="page-state mine-preview-state">加载中...</div>
        <div v-else-if="minePreviewError" class="page-state mine-preview-state">{{ minePreviewError }}</div>

        <div v-else class="mine-preview-body">
          <div class="mine-phone">
            <div class="mine-phone-screen">
              <div class="mine-hero">
                <img class="mine-hero-image" :src="minePreviewBackgroundUrl" alt="主页背景" @error="handleMineBackgroundError" />
                <span class="mine-hero-mask"></span>
              </div>

              <div class="mine-profile-card">
                <div class="mine-profile-top">
                  <div class="mine-avatar">
                    <img
                      v-if="minePreviewProfile.avatarUrl && !minePreviewAvatarError"
                      :src="resolveAvatarUrl(minePreviewProfile.avatarUrl)"
                      :alt="minePreviewNickname"
                      @error="handleMineAvatarError"
                    />
                    <span v-else>{{ minePreviewAvatarText }}</span>
                  </div>
                  <div class="mine-profile-main">
                    <div class="mine-profile-name">{{ minePreviewNickname }}</div>
                    <div class="mine-profile-subtitle">{{ minePreviewThemeName }} · 陪伴每一只宠物</div>
                  </div>
                  <span class="mine-profile-arrow">›</span>
                </div>
                <div class="mine-stat-row">
                  <div class="mine-stat">
                    <span class="mine-stat-value">{{ minePreviewLikeCount }}</span>
                    <span class="mine-stat-label">获赞</span>
                  </div>
                  <div class="mine-stat">
                    <span class="mine-stat-value">{{ minePreviewFollowingCount }}</span>
                    <span class="mine-stat-label">关注</span>
                  </div>
                  <div class="mine-stat">
                    <span class="mine-stat-value">{{ minePreviewFollowerCount }}</span>
                    <span class="mine-stat-label">粉丝</span>
                  </div>
                </div>
                <span v-if="minePreviewProfile.official" class="mine-official-tag">{{ minePreviewProfile.officialTitle || "官方号" }}</span>
              </div>

              <div class="mine-section">
                <div class="mine-section-head">
                  <span class="mine-section-title">我的宠物</span>
                  <span class="mine-section-meta">{{ minePreviewPets.length }} 只</span>
                </div>
                <div class="mine-pet-row" v-if="minePreviewPets.length > 0">
                  <div class="mine-pet-card" v-for="pet in minePreviewPets" :key="pet.id">
                    <img
                      v-if="pet.avatarUrl && !pet._avatarFailed"
                      class="mine-pet-avatar"
                      :src="resolveAvatarUrl(pet.avatarUrl)"
                      :alt="pet.name"
                      @error="handleMinePetAvatarError(pet)"
                    />
                    <span v-else class="mine-pet-avatar mine-pet-avatar--fallback">{{ petInitial(pet) }}</span>
                    <div class="mine-pet-copy">
                      <span class="mine-pet-name">{{ pet.name || "未命名宠物" }}</span>
                      <span class="mine-pet-species">{{ pet.species || "未填写品类" }}</span>
                    </div>
                  </div>
                </div>
                <div class="mine-pet-empty" v-else>
                  <span class="mine-pet-empty-plus">+</span>
                  <span class="mine-pet-empty-text">还没有宠物，先添加一只</span>
                </div>
              </div>

              <div class="mine-section">
                <div class="mine-section-head"><span class="mine-section-title">个人功能</span></div>
                <div class="mine-quick-panel">
                  <div class="mine-quick-card" v-for="item in mineQuickItems" :key="item.label">
                    <img class="mine-quick-icon" :src="item.icon" :alt="item.label" />
                    <span class="mine-quick-title">{{ item.label }}</span>
                  </div>
                </div>
              </div>

              <div class="mine-section">
                <div class="mine-section-head"><span class="mine-section-title">更多服务</span></div>
                <div class="mine-menu-card">
                  <div
                    class="mine-menu-row"
                    :class="{ 'mine-menu-row--last': index === mineMenuItems.length - 1 }"
                    v-for="(row, index) in mineMenuItems"
                    :key="row.label"
                  >
                    <span class="mine-menu-icon" :class="{ 'mine-menu-icon--danger': row.danger }">
                      <img v-if="row.icon" :src="row.icon" :alt="row.label" />
                      <b v-else>!</b>
                    </span>
                    <span class="mine-menu-main">
                      <span class="mine-menu-title" :class="{ 'mine-menu-title--danger': row.danger }">
                        {{ row.label }}
                        <em v-if="row.badge" class="mine-menu-badge">{{ row.badge }}</em>
                      </span>
                      <span class="mine-menu-desc" v-if="row.desc">{{ row.desc }}</span>
                    </span>
                    <span class="mine-menu-arrow" :class="{ 'mine-menu-arrow--danger': row.danger }">›</span>
                  </div>
                </div>
              </div>

              <div class="mine-version-card">
                <div>
                  <div class="mine-version-title">爬仔日记</div>
                  <div class="mine-version-desc">当前版本 1.0.0</div>
                </div>
                <span class="mine-version-badge">服务中</span>
              </div>
            </div>
          </div>

          <div class="mine-preview-side">
            <div class="mine-preview-card">
              <div class="mine-preview-card__title">该用户资料</div>
              <div class="mine-preview-field"><span>昵称</span><strong>{{ minePreviewNickname }}</strong></div>
              <div class="mine-preview-field"><span>UID</span><strong>{{ minePreviewUser ? minePreviewUser.id : "-" }}</strong></div>
              <div class="mine-preview-field"><span>OpenID</span><strong class="mine-preview-openid">{{ minePreviewOpenId || "-" }}</strong></div>
              <div class="mine-preview-field"><span>当前主题</span><strong>{{ minePreviewThemeName }}</strong></div>
              <div class="mine-preview-field"><span>账号状态</span><strong>{{ minePreviewStatusText }}</strong></div>
            </div>

            <div class="mine-preview-card">
              <div class="mine-preview-card__title">移动端可见数据</div>
              <div class="mine-preview-metrics">
                <div class="mine-preview-metric"><strong>{{ minePreviewPetCount }}</strong><span>宠物</span></div>
                <div class="mine-preview-metric"><strong>{{ minePreviewPostCount }}</strong><span>帖子</span></div>
                <div class="mine-preview-metric"><strong>{{ minePreviewAlbumCount }}</strong><span>相册</span></div>
                <div class="mine-preview-metric"><strong>{{ minePreviewLikeCount }}</strong><span>获赞</span></div>
                <div class="mine-preview-metric"><strong>{{ minePreviewFollowingCount }}</strong><span>关注</span></div>
                <div class="mine-preview-metric"><strong>{{ minePreviewFollowerCount }}</strong><span>粉丝</span></div>
              </div>
            </div>

            <div class="mine-preview-note">
              这是按小程序「我的」页面还原的只读预览，点按不会真的跳转；页面里的数字和宠物都来自该用户当前的真实数据。
            </div>
          </div>
        </div>

        <div class="user-pet-modal__foot">
          <span>{{ minePreviewFooterText }}</span>
          <button class="ghost-button" type="button" @click="closeMinePreview">关闭</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  clearAdminUserData,
  deleteAdminUser,
  exportAdminUsers,
  fetchAdminUserList,
  fetchAdminUserPets,
  fetchPetCareRecords,
  fetchPetCostRecords,
  fetchPetCostSummary,
  fetchPetFeedingPlan,
  fetchPetWeightRecords,
  fetchUserCurrentTheme,
  fetchUserPublicProfile,
  restoreAdminUser,
  uploadAdminImage,
  updateAdminUser,
  updateAdminUserStatus
} from "../api/user"
import {
  createPetForUser,
  fetchPublicPetTypeTree,
  parsePetsFromImage,
  uploadPetImage
} from "../api/pet"
import { resolveBackendAssetUrl } from "../utils/apiEndpoint"
import { hasAdminPermission } from "../utils/adminSession"

// 小程序「我的」页面用到的图标与背景图，和管理端还原预览保持一致
const MINE_DELIVERY_ICONS = {
  family: "https://md.ddcloudrack.com:9999/uploads/office-design/1/assets/202608/4ebc014b8add4ba38262d8b8e1badfab.png",
  profile: "https://md.ddcloudrack.com:9999/uploads/office-design/1/assets/202608/ba00e3727ea54865bd9d188d49e6e578.png",
  message: "https://md.ddcloudrack.com:9999/uploads/office-design/1/assets/202608/4e4ddc65c3ea43ce94f175a31e913329.png",
  overview: "https://md.ddcloudrack.com:9999/uploads/theme-icons/20260802140909-4306c4e921444096be23279db4409180.png",
  posts: "https://md.ddcloudrack.com:9999/uploads/office-design/1/assets/202608/48191b21c15544e4a9af5b53fffb1c90.png",
  liked: "https://md.ddcloudrack.com:9999/uploads/office-design/1/assets/202608/20aa434ade87478a9d6036c114862703.png",
  favorite: "https://md.ddcloudrack.com:9999/uploads/office-design/1/assets/202608/bd00d31ed6f646d0933d8a50b8324423.png",
  feedback: "https://md.ddcloudrack.com:9999/uploads/theme-icons/20260802140909-3eb5be39326e49e7a3d1f2c7a4db1887.png",
  privacy: "https://md.ddcloudrack.com:9999/uploads/theme-icons/20260802140909-714fe9d869a042d8bd8ca3c73bc0e93a.png",
  about: "https://md.ddcloudrack.com:9999/uploads/theme-icons/20260802140908-fca0b7aae4c044d6a71d081d7b14fc40.png",
  recycle: "https://md.ddcloudrack.com:9999/uploads/theme-icons/20260802140909-3b3f8970701040eba7d64efb3f66a0c4.png"
}

const MINE_PROFILE_BACKGROUNDS = {
  repti_lover: "https://md.ddcloudrack.com:9999/uploads/office-design/1/screens/202608/4f1b58983596485e8f7d947ee0384447.png",
  default: "https://md.ddcloudrack.com:9999/uploads/office-design/2/screens/202608/444377682ad548daafc09ae8aa3db971.png"
}

export default {
  name: "UserListView",
  data() {
    return {
      loading: false,
      users: [],
      keyword: "",
      statusFilter: "",
      showDeleted: false,
      exporting: false,
      currentPage: 1,
      pageSize: 20,
      total: 0,
      totalPages: 1,
      userEditorVisible: false,
      userSaving: false,
      userAvatarUploading: false,
      userEditorError: "",
      userForm: this.emptyUserForm(),
      confirmVisible: false,
      confirmMode: "delete",
      confirmUser: null,
      confirmChecked: false,
      confirmSaving: false,
      confirmError: "",
      toast: "",
      toastError: false,
      toastTimer: null,
      petModalVisible: false,
      petModalLoading: false,
      petModalUser: null,
      petModalPets: [],
      petModalError: "",
      petDetailVisible: false,
      petDetailLoading: false,
      petDetailError: "",
      petDetailType: "profile",
      activePet: null,
      petDetailData: this.emptyPetDetailData(),
      copiedUserId: null,
      keywordTimer: null,
      petCreateVisible: false,
      petCreateUser: null,
      petCreateTab: "form",
      petCreateNotice: "",
      petTypeLevel1Options: [],
      petTypeChildrenMap: {},
      petTypeNodeMap: {},
      petForm: this.emptyPetForm(),
      petFormSubmitting: false,
      petFormUploading: { avatarUrl: false, coverUrl: false },
      aiImageFile: null,
      aiImagePreviewUrl: "",
      aiParsing: false,
      aiParsed: false,
      aiParseError: "",
      aiSummary: "",
      aiParsedImageUrl: "",
      aiDrafts: [],
      aiUseImageAsMedia: true,
      aiSubmitting: false,
      aiDraftKeySeed: 0,
      minePreviewVisible: false,
      minePreviewLoading: false,
      minePreviewError: "",
      minePreviewUser: null,
      minePreviewProfile: {},
      minePreviewPets: [],
      minePreviewThemeName: "默认主题",
      minePreviewThemeCode: "",
      minePreviewBackgroundError: false,
      minePreviewAvatarError: false,
      mineQuickItems: [
        { label: "家庭成员", icon: MINE_DELIVERY_ICONS.family },
        { label: "个人信息", icon: MINE_DELIVERY_ICONS.profile }
      ],
      mineMenuItems: [
        { label: "消息", desc: "广场互动、审核结果和系统通知", icon: MINE_DELIVERY_ICONS.message, badge: "" },
        { label: "数据总览", desc: "查看养护数据", icon: MINE_DELIVERY_ICONS.overview, badge: "" },
        { label: "我发布的内容", desc: "管理你发布的帖子", icon: MINE_DELIVERY_ICONS.posts, badge: "" },
        { label: "我点赞的", desc: "查看点赞过的广场内容", icon: MINE_DELIVERY_ICONS.liked, badge: "" },
        { label: "我收藏的", desc: "查看收藏的广场内容", icon: MINE_DELIVERY_ICONS.favorite, badge: "" },
        { label: "问题反馈", desc: "", icon: MINE_DELIVERY_ICONS.feedback, badge: "" },
        { label: "隐私协议", desc: "", icon: MINE_DELIVERY_ICONS.privacy, badge: "" },
        { label: "关于我们", desc: "", icon: MINE_DELIVERY_ICONS.about, badge: "" },
        { label: "宠物回收站", desc: "恢复已删除的宠物资料", icon: MINE_DELIVERY_ICONS.recycle, badge: "" },
        { label: "一键清空数据", desc: "删除宠物、家庭及账号相关数据", icon: "", badge: "", danger: true }
      ]
    }
  },
  computed: {
    canEditUser() {
      return hasAdminPermission("users:edit")
    },
    canDeleteUser() {
      return hasAdminPermission("users:delete")
    },
    emptyStateText() {
      if (this.keyword || this.statusFilter !== "" || this.showDeleted) {
        return "没有匹配的用户"
      }
      return "暂无用户数据"
    },
    minePreviewNickname() {
      return this.minePreviewProfile.nickname || this.displayUserName(this.minePreviewUser)
    },
    minePreviewAvatarText() {
      const name = this.minePreviewNickname || "爬"
      return name.slice(0, 1)
    },
    minePreviewOpenId() {
      return this.minePreviewProfile.userOpenId || (this.minePreviewUser && this.minePreviewUser.openId) || ""
    },
    minePreviewPetCount() {
      return Number(this.minePreviewProfile.petCount || this.minePreviewPets.length || 0)
    },
    minePreviewPostCount() {
      return Number(this.minePreviewProfile.postCount || 0)
    },
    minePreviewAlbumCount() {
      return Number(this.minePreviewProfile.albumCount || 0)
    },
    minePreviewLikeCount() {
      return Number(this.minePreviewProfile.receivedLikeCount || 0)
    },
    minePreviewFollowingCount() {
      return Number(this.minePreviewProfile.followingCount || 0)
    },
    minePreviewFollowerCount() {
      return Number(this.minePreviewProfile.followerCount || 0)
    },
    minePreviewBackgroundUrl() {
      const custom = this.minePreviewProfile.profileBackgroundUrl
      if (custom && !this.minePreviewBackgroundError) {
        return this.resolveAvatarUrl(custom)
      }
      if (this.minePreviewThemeCode === "repti_lover") {
        return MINE_PROFILE_BACKGROUNDS.repti_lover
      }
      return MINE_PROFILE_BACKGROUNDS.default
    },
    minePreviewStatusText() {
      if (!this.minePreviewUser) {
        return "-"
      }
      if (this.minePreviewUser.deleted === 1) {
        return "已删除"
      }
      return this.minePreviewUser.status === 1 ? "正常" : "已停用"
    },
    minePreviewFooterText() {
      if (this.minePreviewLoading || this.minePreviewError) {
        return "我的界面预览"
      }
      return `UID ${this.minePreviewUser ? this.minePreviewUser.id : "-"} · ${this.minePreviewPetCount} 只宠物 · ${this.minePreviewThemeName}`
    },
    confirmTitle() {
      return this.confirmMode === "clear" ? "清空用户数据" : "删除用户"
    },
    confirmDesc() {
      if (!this.confirmUser) {
        return ""
      }
      return `${this.displayUserName(this.confirmUser)} · UID ${this.confirmUser.id}`
    },
    confirmTargetText() {
      if (!this.confirmUser) {
        return ""
      }
      return `该用户当前有 ${this.confirmUser.petCount || 0} 只宠物`
    },
    confirmActionLabel() {
      return this.confirmMode === "clear" ? "确认清空数据" : "确认删除"
    },
    confirmCheckLabel() {
      if (this.confirmMode === "clear") {
        return "我确认清空该用户的宠物、记录与广场内容"
      }
      return "我确认删除该用户，删除后可在回收站恢复"
    },
    confirmNotes() {
      if (this.confirmMode === "clear") {
        return [
          "宠物的照护、体重、成本、喂食计划等记录会被清空。",
          "广场动态、收藏、关注关系会一并移除。",
          "用户资料会回到初始状态，账号本身保留，可以继续登录。",
          "清空后无法通过后台恢复，请谨慎操作。"
        ]
      }
      return [
        "删除后该用户无法再登录小程序，列表中也会隐藏。",
        "宠物与记录数据会保留，勾选“包含已删除”后可以随时恢复。",
        "如需彻底清除数据，请使用“清空数据”操作。"
      ]
    },
    pageStart() {
      if (this.total === 0) {
        return 0
      }
      return (this.currentPage - 1) * this.pageSize + 1
    },
    pageEnd() {
      return Math.min(this.currentPage * this.pageSize, this.total)
    },
    visiblePages() {
      const pages = []
      const start = Math.max(1, this.currentPage - 2)
      const end = Math.min(this.totalPages, start + 4)
      const adjustedStart = Math.max(1, end - 4)
      for (let page = adjustedStart; page <= end; page += 1) {
        pages.push(page)
      }
      return pages
    },
    canLoadPetData() {
      return Boolean(this.petModalUser && this.petModalUser.openId)
    },
    petDetailTitle() {
      const titleMap = {
        profile: "宠物详细资料",
        care: "照护数据",
        weight: "体重数据",
        cost: "成本数据",
        plan: "喂养计划"
      }
      return titleMap[this.petDetailType] || "宠物数据"
    },
    petDetailFooter() {
      if (!this.activePet) {
        return "宠物数据"
      }
      if (this.petDetailType === "care") {
        return `最近 ${this.petDetailData.careRecords.length} 条照护记录`
      }
      if (this.petDetailType === "weight") {
        return `最近 ${this.petDetailData.weightRecords.length} 条体重记录`
      }
      if (this.petDetailType === "cost") {
        return `最近 ${this.petDetailData.costRecords.length} 条成本记录`
      }
      return `宠物 ID ${this.activePet.id}`
    },
    petProfileFields() {
      if (!this.activePet) {
        return []
      }
      return [
        { label: "宠物 ID", value: this.formatText(this.activePet.id) },
        { label: "所属用户", value: this.formatText(this.activePet.ownerUserId) },
        { label: "品类编码", value: this.formatText(this.activePet.speciesCode) },
        { label: "品类分组", value: this.formatText(this.activePet.speciesGroupCode) },
        { label: "性别", value: this.formatText(this.activePet.sex) },
        { label: "阶段", value: this.formatText(this.activePet.stage) },
        { label: "当前体重", value: this.formatWeight(this.activePet.currentWeight) },
        { label: "到家体重", value: this.formatWeight(this.activePet.arrivalWeight) },
        { label: "孵化日期", value: this.formatDate(this.activePet.hatchDate) },
        { label: "到家日期", value: this.formatDate(this.activePet.adoptDate) },
        { label: "标签", value: this.formatText(this.activePet.careTags) },
        { label: "留种", value: this.activePet.holdbackFlag === 1 ? "是" : "否" },
        { label: "销售状态", value: this.formatText(this.activePet.saleStatus) },
        { label: "状态", value: this.activePet.status === 1 ? "正常" : "停用" },
        { label: "创建时间", value: this.formatDate(this.activePet.createdAt) },
        { label: "更新时间", value: this.formatDate(this.activePet.updatedAt) }
      ]
    },
    feedingManualPlanText() {
      const plan = this.petDetailData.feedingPlan || {}
      if (!plan.hasManualPlan) {
        return "暂无手动计划"
      }
      const parts = [
        plan.manualFrequency,
        plan.manualPortion,
        plan.manualFoods,
        plan.manualSupplements,
        plan.manualObservation,
        plan.manualNotes
      ].filter(Boolean)
      return parts.length > 0 ? parts.join(" / ") : "已配置手动计划"
    },
    petTypeLevel2Options() {
      return this.petTypeOptionsByParent(this.petForm.speciesGroupCode)
    },
    petTypeLevel3Options() {
      return this.petTypeOptionsByParent(this.petForm.speciesCode)
    },
    checkedAiDraftCount() {
      return this.aiDrafts.filter(item => item.checked && !item.saved).length
    },
    allAiDraftsChecked() {
      return this.aiDrafts.length > 0 && this.aiDrafts.every(item => item.checked)
    },
    petCreateHint() {
      if (this.petCreateTab === "image") {
        if (this.aiParsing) {
          return "AI 正在识别图片，请稍候。"
        }
        if (this.aiDrafts.length > 0) {
          return "确认无误后再点“确认添加”，没有勾选的候选项不会保存。"
        }
        return "先上传图片，再点“开始识别”。"
      }
      if (!this.petForm.name && !this.petForm.species) {
        return "宠物名字和品类至少填写一个。"
      }
      return "保存后会直接写入该用户的宠物档案。"
    }
  },
  beforeDestroy() {
    if (this.keywordTimer) {
      clearTimeout(this.keywordTimer)
      this.keywordTimer = null
    }
    if (this.toastTimer) {
      clearTimeout(this.toastTimer)
      this.toastTimer = null
    }
    if (this.aiImagePreviewUrl) {
      URL.revokeObjectURL(this.aiImagePreviewUrl)
      this.aiImagePreviewUrl = ""
    }
  },
  created() {
    this.loadUsers()
  },
  methods: {
    async loadUsers() {
      this.loading = true
      try {
        const res = await fetchAdminUserList({
          keyword: this.keyword || undefined,
          status: this.statusFilter === "" ? undefined : Number(this.statusFilter),
          includeDeleted: this.showDeleted ? true : undefined,
          page: this.currentPage,
          pageSize: this.pageSize
        })
        const data = res.data || {}
        this.users = data.records || []
        this.total = data.total || 0
        this.totalPages = Math.max(1, data.totalPages || 1)
        this.ensureCurrentPageInRange()
      } catch (error) {
        this.showToast(error && error.message ? error.message : "用户列表加载失败", true)
      } finally {
        this.loading = false
      }
    },
    emptyUserForm() {
      return {
        id: null,
        openId: "",
        nickname: "",
        phone: "",
        avatarUrl: "",
        status: 1,
        petCount: 0,
        official: 0,
        officialTitle: "",
        wechatBound: false,
        officialBound: false,
        createdAt: ""
      }
    },
    isDeletedUser(user) {
      return Boolean(user) && user.deleted === 1
    },
    statusLabel(user) {
      if (!user) {
        return "-"
      }
      if (this.isDeletedUser(user)) {
        return "回收站"
      }
      return user.status === 1 ? "正常" : "已停用"
    },
    statusClass(user) {
      if (!user || this.isDeletedUser(user)) {
        return "user-status-tag--deleted"
      }
      return user.status === 1 ? "user-status-tag--normal" : "user-status-tag--disabled"
    },
    formatCreatedAt(value) {
      if (!value) {
        return "-"
      }
      return String(value).replace("T", " ").slice(0, 16)
    },
    openUserEditor(user) {
      if (!user || !user.id) {
        return
      }
      this.userForm = {
        id: user.id,
        openId: user.openId || "",
        nickname: user.nickname || "",
        phone: user.phone || "",
        avatarUrl: user.avatarUrl || "",
        status: user.status === 0 ? 0 : 1,
        petCount: user.petCount || 0,
        official: user.official || 0,
        officialTitle: user.officialTitle || "",
        wechatBound: Boolean(user.wechatBound),
        officialBound: Boolean(user.officialBound),
        createdAt: user.createdAt || ""
      }
      this.userEditorError = ""
      this.userEditorVisible = true
    },
    closeUserEditor() {
      if (this.userSaving) {
        return
      }
      this.userEditorVisible = false
      this.userEditorError = ""
    },
    async pickUserAvatar(event) {
      const file = event && event.target && event.target.files ? event.target.files[0] : null
      if (!file) {
        return
      }
      this.userAvatarUploading = true
      this.userEditorError = ""
      try {
        const res = await uploadAdminImage(file)
        const url = res && res.data ? res.data.url : ""
        if (url) {
          this.userForm.avatarUrl = url
        }
      } catch (error) {
        this.userEditorError = error && error.message ? error.message : "头像上传失败"
      } finally {
        this.userAvatarUploading = false
        if (event && event.target) {
          event.target.value = ""
        }
      }
    },
    async submitUserEditor() {
      const nickname = String(this.userForm.nickname || "").trim()
      if (!nickname) {
        this.userEditorError = "昵称不能为空"
        return
      }
      this.userSaving = true
      this.userEditorError = ""
      try {
        await updateAdminUser(this.userForm.id, {
          nickname,
          phone: String(this.userForm.phone || "").trim() || null,
          avatarUrl: String(this.userForm.avatarUrl || "").trim() || null,
          status: this.userForm.status
        })
        this.userEditorVisible = false
        await this.loadUsers()
        this.showToast("用户资料已保存")
      } catch (error) {
        this.userEditorError = error && error.message ? error.message : "保存失败"
      } finally {
        this.userSaving = false
      }
    },
    async toggleUserStatus(user) {
      if (!user || !user.id || !this.canEditUser) {
        return
      }
      const nextStatus = user.status === 1 ? 0 : 1
      try {
        await updateAdminUserStatus(user.id, nextStatus)
        await this.loadUsers()
        this.showToast(nextStatus === 1 ? "已启用该用户" : "已停用该用户")
      } catch (error) {
        this.showToast(error && error.message ? error.message : "账号状态更新失败", true)
      }
    },
    openDelete(user) {
      if (!user || !user.id || !this.canDeleteUser) {
        return
      }
      this.confirmMode = "delete"
      this.confirmUser = user
      this.confirmChecked = false
      this.confirmError = ""
      this.confirmVisible = true
    },
    openClearData(user) {
      if (!user || !user.id || !this.canDeleteUser) {
        return
      }
      this.confirmMode = "clear"
      this.confirmUser = user
      this.confirmChecked = false
      this.confirmError = ""
      this.confirmVisible = true
    },
    closeConfirm() {
      if (this.confirmSaving) {
        return
      }
      this.confirmVisible = false
      this.confirmUser = null
      this.confirmChecked = false
      this.confirmError = ""
    },
    async runConfirmAction() {
      if (!this.confirmUser || !this.confirmChecked) {
        return
      }
      const target = this.confirmUser
      const mode = this.confirmMode
      this.confirmSaving = true
      this.confirmError = ""
      try {
        if (mode === "clear") {
          await clearAdminUserData(target.id)
        } else {
          await deleteAdminUser(target.id)
        }
        this.confirmVisible = false
        this.confirmUser = null
        this.confirmChecked = false
        await this.loadUsers()
        this.showToast(mode === "clear" ? "已清空该用户的业务数据" : "用户已删除，勾选“包含已删除”可恢复")
      } catch (error) {
        this.confirmError = error && error.message ? error.message : "操作失败"
      } finally {
        this.confirmSaving = false
      }
    },
    async restoreUser(user) {
      if (!user || !user.id || !this.canDeleteUser) {
        return
      }
      try {
        await restoreAdminUser(user.id)
        await this.loadUsers()
        this.showToast("用户已恢复")
      } catch (error) {
        this.showToast(error && error.message ? error.message : "恢复失败", true)
      }
    },
    async exportUsers() {
      this.exporting = true
      try {
        const blob = await exportAdminUsers({
          keyword: this.keyword || undefined,
          status: this.statusFilter === "" ? undefined : Number(this.statusFilter),
          includeDeleted: this.showDeleted ? true : undefined
        })
        if (!(blob instanceof Blob)) {
          throw new Error("导出内容异常，请稍后重试")
        }
        if (String(blob.type || "").indexOf("json") >= 0) {
          const text = await blob.text()
          let message = "导出失败，请稍后重试"
          try {
            const payload = JSON.parse(text)
            if (payload && payload.message) {
              message = payload.message
            }
          } catch (parseError) {
            message = "导出失败，请稍后重试"
          }
          throw new Error(message)
        }
        const url = URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = url
        link.download = `users-${this.exportStamp()}.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        setTimeout(() => URL.revokeObjectURL(url), 1000)
        this.showToast("已开始下载用户数据")
      } catch (error) {
        this.showToast(error && error.message ? error.message : "导出失败", true)
      } finally {
        this.exporting = false
      }
    },
    exportStamp() {
      const now = new Date()
      const pad = value => String(value).padStart(2, "0")
      return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`
    },
    handleFilterChange() {
      this.currentPage = 1
      this.loadUsers()
    },
    showToast(message, error = false) {
      if (this.toastTimer) {
        clearTimeout(this.toastTimer)
      }
      this.toast = message
      this.toastError = error
      this.toastTimer = setTimeout(() => {
        this.toast = ""
        this.toastTimer = null
      }, 3000)
    },
    async openPetModal(user) {
      if (!user || !user.id) {
        return
      }
      this.petModalUser = user
      this.petModalVisible = true
      this.petModalLoading = true
      this.petModalPets = []
      this.petModalError = ""
      try {
        const res = await fetchAdminUserPets(user.id)
        this.petModalPets = res.data || []
      } catch (error) {
        this.petModalError = error && error.message ? error.message : "宠物加载失败"
      } finally {
        this.petModalLoading = false
      }
    },
    closePetModal() {
      this.petModalVisible = false
      this.petModalUser = null
      this.petModalPets = []
      this.petModalError = ""
      this.petModalLoading = false
      this.closePetDetail()
    },
    isPetModalLoadingFor(user) {
      return this.petModalLoading && this.petModalUser && this.petModalUser.id === user.id
    },
    async openMinePreview(user) {
      if (!user || !user.id) {
        return
      }
      this.minePreviewUser = user
      this.minePreviewVisible = true
      this.minePreviewLoading = true
      this.minePreviewError = ""
      this.minePreviewProfile = {}
      this.minePreviewPets = []
      this.minePreviewThemeName = "默认主题"
      this.minePreviewThemeCode = ""
      this.minePreviewBackgroundError = false
      this.minePreviewAvatarError = false
      try {
        const res = await fetchUserPublicProfile(user.id)
        const profile = res.data || {}
        this.minePreviewProfile = profile
        this.minePreviewPets = (profile.pets || []).map(pet => ({
          ...pet,
          _avatarFailed: false
        }))
      } catch (error) {
        this.minePreviewError = (error && error.message) || "读取用户资料失败"
        this.minePreviewLoading = false
        return
      }
      if (user.openId) {
        try {
          const themeRes = await fetchUserCurrentTheme(user.openId)
          const themeData = themeRes.data || {}
          const theme = themeData.theme || {}
          if (theme.themeName) {
            this.minePreviewThemeName = theme.themeName
          }
          if (theme.themeCode) {
            this.minePreviewThemeCode = theme.themeCode
          }
        } catch (error) {
          this.minePreviewThemeName = "默认主题"
        }
      }
      this.minePreviewLoading = false
    },
    handleMineBackgroundError() {
      if (!this.minePreviewBackgroundError) {
        this.minePreviewBackgroundError = true
      }
    },
    handleMineAvatarError() {
      if (!this.minePreviewAvatarError) {
        this.minePreviewAvatarError = true
      }
    },
    handleMinePetAvatarError(pet) {
      if (pet && !pet._avatarFailed) {
        this.$set(pet, "_avatarFailed", true)
      }
    },
    closeMinePreview() {
      this.minePreviewVisible = false
      this.minePreviewLoading = false
      this.minePreviewError = ""
      this.minePreviewUser = null
      this.minePreviewProfile = {}
      this.minePreviewPets = []
      this.minePreviewBackgroundError = false
      this.minePreviewAvatarError = false
    },
    emptyPetForm() {
      return {
        name: "",
        speciesGroupCode: "",
        speciesCode: "",
        speciesDetailCode: "",
        species: "",
        morph: "",
        sex: "",
        stage: "",
        currentWeight: "",
        arrivalWeight: "",
        bodyLengthCm: "",
        hatchDate: "",
        adoptDate: "",
        careTags: "",
        intro: "",
        avatarUrl: "",
        coverUrl: ""
      }
    },
    openPetCreate(user) {
      if (!user || !user.id) {
        return
      }
      this.petCreateUser = user
      this.petCreateVisible = true
      this.petCreateNotice = ""
      this.petCreateTab = "form"
      this.petForm = this.emptyPetForm()
      this.resetAiParse()
      this.loadPetTypeTree()
    },
    closePetCreate() {
      if (this.petFormSubmitting || this.aiSubmitting) {
        return
      }
      this.petCreateVisible = false
      this.petCreateUser = null
      this.petCreateNotice = ""
      this.petForm = this.emptyPetForm()
      this.resetAiParse()
    },
    switchPetCreateTab(tab) {
      this.petCreateTab = tab === "image" ? "image" : "form"
    },
    async loadPetTypeTree() {
      if (this.petTypeLevel1Options.length > 0) {
        return
      }
      try {
        const res = await fetchPublicPetTypeTree()
        const tree = res.data || {}
        this.buildPetTypeIndex(tree.items || [])
      } catch (error) {
        this.petTypeLevel1Options = []
        this.petTypeChildrenMap = {}
        this.petTypeNodeMap = {}
      }
    },
    buildPetTypeIndex(nodes) {
      const level1 = []
      const childrenMap = {}
      const nodeMap = {}
      const walk = list => {
        ;(list || []).forEach(node => {
          if (!node || !node.typeCode) {
            return
          }
          nodeMap[node.typeCode] = node
          if (node.levelNo === 1) {
            level1.push(node)
          }
          const parentKey = node.parentTypeCode || ""
          if (!childrenMap[parentKey]) {
            childrenMap[parentKey] = []
          }
          childrenMap[parentKey].push(node)
          walk(node.children)
        })
      }
      walk(nodes)
      this.petTypeLevel1Options = level1
      this.petTypeChildrenMap = childrenMap
      this.petTypeNodeMap = nodeMap
    },
    petTypeOptionsByParent(parentCode) {
      if (!parentCode) {
        return []
      }
      const map = this.petTypeChildrenMap || {}
      return map[parentCode] || []
    },
    petTypeLevel2OptionsFor(groupCode) {
      return this.petTypeOptionsByParent(groupCode)
    },
    petTypeNodeName(typeCode) {
      const node = this.petTypeNodeMap ? this.petTypeNodeMap[typeCode] : null
      return node && node.typeName ? node.typeName : ""
    },
    handlePetFormGroupChange() {
      this.petForm.speciesCode = ""
      this.petForm.speciesDetailCode = ""
      this.petForm.species = this.petTypeNodeName(this.petForm.speciesGroupCode)
    },
    handlePetFormSpeciesChange() {
      this.petForm.speciesDetailCode = ""
      this.petForm.species = this.petTypeNodeName(this.petForm.speciesCode) || this.petTypeNodeName(this.petForm.speciesGroupCode)
    },
    handleAiDraftGroupChange(draft) {
      if (!draft) {
        return
      }
      draft.speciesCode = ""
      draft.speciesDetailCode = ""
      draft.species = this.petTypeNodeName(draft.speciesGroupCode)
    },
    handleAiDraftSpeciesChange(draft) {
      if (!draft) {
        return
      }
      draft.speciesDetailCode = ""
      draft.species = this.petTypeNodeName(draft.speciesCode) || this.petTypeNodeName(draft.speciesGroupCode)
    },
    triggerMediaPick(refName) {
      const input = this.$refs[refName]
      if (input && input.click) {
        input.click()
      }
    },
    setPetFormMedia(field, value) {
      this.$set(this.petForm, field, value)
    },
    async handlePetFormMediaPick(event, field) {
      const files = event && event.target && event.target.files ? event.target.files : null
      const file = files && files.length > 0 ? files[0] : null
      if (event && event.target) {
        event.target.value = ""
      }
      if (!file) {
        return
      }
      if (!/^image\//i.test(file.type || "")) {
        this.petCreateNotice = "只能上传图片文件。"
        return
      }
      this.$set(this.petFormUploading, field, true)
      this.petCreateNotice = ""
      try {
        const res = await uploadPetImage(file)
        const data = res.data || {}
        this.setPetFormMedia(field, data.url || "")
      } catch (error) {
        this.petCreateNotice = (error && error.message) || "图片上传失败，请重试。"
      } finally {
        this.$set(this.petFormUploading, field, false)
      }
    },
    handleAiImagePick(event) {
      const files = event && event.target && event.target.files ? event.target.files : null
      const file = files && files.length > 0 ? files[0] : null
      if (event && event.target) {
        event.target.value = ""
      }
      if (!file) {
        return
      }
      if (!/^image\//i.test(file.type || "")) {
        this.aiParseError = "只能上传图片文件。"
        return
      }
      if (this.aiImagePreviewUrl) {
        URL.revokeObjectURL(this.aiImagePreviewUrl)
      }
      this.aiImageFile = file
      this.aiImagePreviewUrl = URL.createObjectURL(file)
      this.aiParsed = false
      this.aiParseError = ""
      this.aiSummary = ""
      this.aiParsedImageUrl = ""
      this.aiDrafts = []
    },
    resetAiParse() {
      if (this.aiImagePreviewUrl) {
        URL.revokeObjectURL(this.aiImagePreviewUrl)
      }
      this.aiImageFile = null
      this.aiImagePreviewUrl = ""
      this.aiParsing = false
      this.aiParsed = false
      this.aiParseError = ""
      this.aiSummary = ""
      this.aiParsedImageUrl = ""
      this.aiDrafts = []
      this.aiSubmitting = false
    },
    async startAiParse() {
      if (!this.aiImageFile || this.aiParsing) {
        return
      }
      this.aiParsing = true
      this.aiParseError = ""
      this.aiSummary = ""
      this.aiParsed = false
      this.aiDrafts = []
      try {
        const res = await parsePetsFromImage(this.aiImageFile)
        const data = res.data || {}
        this.aiSummary = data.summary || ""
        this.aiParsedImageUrl = data.imageUrl || ""
        this.aiDrafts = (data.items || []).map((item, index) => this.buildAiDraft(item, index))
        this.aiUseImageAsMedia = this.aiDrafts.length === 1
        this.aiParsed = true
      } catch (error) {
        this.aiParseError = (error && error.message) || "AI 识别失败，请换一张图片再试。"
      } finally {
        this.aiParsing = false
      }
    },
    buildAiDraft(item, index) {
      const source = item || {}
      this.aiDraftKeySeed += 1
      return {
        _key: `pet-draft-${this.aiDraftKeySeed}`,
        checked: true,
        saved: false,
        saving: false,
        name: source.name || "",
        speciesGroupCode: source.speciesGroupCode || "",
        speciesCode: source.speciesCode || "",
        speciesDetailCode: source.speciesDetailCode || "",
        species: source.species || "",
        morph: source.morph || "",
        sex: source.sex === "公" || source.sex === "母" ? source.sex : "",
        stage: source.stage && source.stage !== "未知" ? source.stage : "",
        currentWeight: this.formatNumericInput(source.currentWeight),
        bodyLengthCm: this.formatNumericInput(source.bodyLengthCm),
        hatchDate: source.hatchDate || "",
        adoptDate: source.adoptDate || "",
        intro: source.intro || "",
        careTags: source.careTags || "",
        note: source.note || "",
        confidence: source.confidence || "low",
        index: index + 1
      }
    },
    formatNumericInput(value) {
      if (value === null || typeof value === "undefined" || value === "") {
        return ""
      }
      return String(value)
    },
    toggleAllAiDrafts(checked) {
      this.aiDrafts.forEach(item => {
        if (!item.saved) {
          item.checked = Boolean(checked)
        }
      })
    },
    confidenceLabel(confidence) {
      const map = {
        high: "把握较高",
        medium: "大致判断",
        low: "信息偏少"
      }
      return map[String(confidence || "").toLowerCase()] || "信息偏少"
    },
    toNumberOrNull(value) {
      const text = value === null || typeof value === "undefined" ? "" : String(value).trim()
      if (!text) {
        return null
      }
      const parsed = Number(text)
      return Number.isFinite(parsed) ? parsed : null
    },
    buildPetPayload(source, mediaUrl) {
      return {
        ownerUserId: this.petCreateUser ? this.petCreateUser.id : null,
        name: source.name || null,
        speciesGroupCode: source.speciesGroupCode || null,
        speciesCode: source.speciesCode || null,
        speciesDetailCode: source.speciesDetailCode || null,
        species: source.species || null,
        morph: source.morph || null,
        sex: source.sex || null,
        stage: source.stage || null,
        currentWeight: this.toNumberOrNull(source.currentWeight),
        arrivalWeight: this.toNumberOrNull(source.arrivalWeight),
        bodyLengthCm: this.toNumberOrNull(source.bodyLengthCm),
        hatchDate: source.hatchDate || null,
        adoptDate: source.adoptDate || null,
        avatarUrl: source.avatarUrl || mediaUrl || null,
        coverUrl: source.coverUrl || mediaUrl || null,
        intro: source.intro || null,
        careTags: source.careTags || null
      }
    },
    async submitPetForm() {
      if (this.petFormSubmitting) {
        return
      }
      if (!this.petForm.name && !this.petForm.species) {
        this.petCreateNotice = "请至少填写宠物名字或品类。"
        return
      }
      this.petFormSubmitting = true
      this.petCreateNotice = ""
      try {
        await createPetForUser(this.buildPetPayload(this.petForm, ""))
        this.petForm = this.emptyPetForm()
        this.petCreateNotice = "宠物已添加成功，可以继续添加下一只。"
        await this.refreshAfterPetCreate()
      } catch (error) {
        this.petCreateNotice = (error && error.message) || "保存失败，请重试。"
      } finally {
        this.petFormSubmitting = false
      }
    },
    async createCheckedAiDrafts() {
      const targets = this.aiDrafts.filter(item => item.checked && !item.saved)
      if (targets.length === 0 || this.aiSubmitting) {
        return
      }
      await this.submitAiDrafts(targets)
    },
    async createAiDraft(draft) {
      if (!draft || draft.saving || draft.saved) {
        return
      }
      await this.submitAiDrafts([draft])
    },
    async submitAiDrafts(targets) {
      this.aiSubmitting = true
      this.petCreateNotice = ""
      const mediaUrl = this.aiUseImageAsMedia ? this.aiParsedImageUrl : ""
      let successCount = 0
      const failures = []
      for (const draft of targets) {
        draft.saving = true
        try {
          await createPetForUser(this.buildPetPayload(draft, mediaUrl))
          draft.saved = true
          draft.checked = false
          draft.saving = false
          successCount += 1
        } catch (error) {
          draft.saving = false
          failures.push(`${draft.name || draft.species || "未命名"}：${(error && error.message) || "保存失败"}`)
        }
      }
      this.aiSubmitting = false
      if (successCount > 0) {
        await this.refreshAfterPetCreate()
      }
      if (failures.length > 0) {
        this.petCreateNotice = `已添加 ${successCount} 条，${failures.length} 条失败：${failures.join("；")}`
      } else if (successCount > 0) {
        this.petCreateNotice = `已添加 ${successCount} 只宠物，可以继续确认其他候选项。`
      }
    },
    async refreshAfterPetCreate() {
      await this.loadUsers()
      if (this.petModalVisible && this.petModalUser && this.petCreateUser && this.petModalUser.id === this.petCreateUser.id) {
        try {
          const res = await fetchAdminUserPets(this.petModalUser.id)
          this.petModalPets = res.data || []
        } catch (error) {
          this.petModalError = (error && error.message) || "宠物加载失败"
        }
      }
    },
    displayUserName(user) {
      return user && user.nickname ? user.nickname : "微信用户"
    },
    avatarInitial(user) {
      const name = this.displayUserName(user)
      return name ? name.slice(0, 1).toUpperCase() : "微"
    },
    shouldShowAvatar(user) {
      return Boolean(user && user.avatarUrl && !user._avatarLoadFailed)
    },
    resolveAvatarUrl(avatarUrl) {
      return resolveBackendAssetUrl(avatarUrl)
    },
    handleAvatarError(user) {
      this.$set(user, "_avatarLoadFailed", true)
    },
    emptyPetDetailData() {
      return {
        careRecords: [],
        weightRecords: [],
        costRecords: [],
        costSummary: {},
        feedingPlan: {}
      }
    },
    async openPetDetail(pet, type) {
      if (!pet || !pet.id) {
        return
      }
      this.activePet = pet
      this.petDetailType = type
      this.petDetailVisible = true
      this.petDetailLoading = false
      this.petDetailError = ""
      this.petDetailData = this.emptyPetDetailData()

      if (type === "profile") {
        return
      }
      if (!this.canLoadPetData) {
        this.petDetailError = "该用户缺少 OpenID，无法查询记录类数据"
        return
      }

      this.petDetailLoading = true
      try {
        const openId = this.petModalUser.openId
        if (type === "care") {
          const res = await fetchPetCareRecords(openId, pet.id)
          this.petDetailData.careRecords = res.data || []
        } else if (type === "weight") {
          const res = await fetchPetWeightRecords(openId, pet.id)
          this.petDetailData.weightRecords = res.data || []
        } else if (type === "cost") {
          const [summaryRes, recordsRes] = await Promise.all([
            fetchPetCostSummary(openId, pet.id),
            fetchPetCostRecords(openId, pet.id)
          ])
          this.petDetailData.costSummary = summaryRes.data || {}
          this.petDetailData.costRecords = recordsRes.data || []
        } else if (type === "plan") {
          const ownerUserId = pet.ownerUserId || (this.petModalUser && this.petModalUser.id)
          const res = await fetchPetFeedingPlan(ownerUserId, pet.id)
          this.petDetailData.feedingPlan = res.data || {}
        }
      } catch (error) {
        this.petDetailError = error && error.message ? error.message : "宠物数据加载失败"
      } finally {
        this.petDetailLoading = false
      }
    },
    closePetDetail() {
      this.petDetailVisible = false
      this.petDetailLoading = false
      this.petDetailError = ""
      this.petDetailType = "profile"
      this.activePet = null
      this.petDetailData = this.emptyPetDetailData()
    },
    petInitial(pet) {
      const name = pet && pet.name ? pet.name : "宠"
      return name.slice(0, 1).toUpperCase()
    },
    resolveFileUrl(fileUrl) {
      return this.resolveAvatarUrl(fileUrl)
    },
    formatText(value, fallback = "-") {
      if (value === null || typeof value === "undefined" || value === "") {
        return fallback
      }
      return String(value)
    },
    formatDate(value) {
      if (!value) {
        return "-"
      }
      return String(value).replace("T", " ").slice(0, 19)
    },
    formatWeight(value) {
      if (value === null || typeof value === "undefined" || value === "") {
        return "-"
      }
      return `${value} g`
    },
    formatMoney(value) {
      const amount = Number(value || 0)
      return `¥${amount.toFixed(2)}`
    },
    recordTypeLabel(type) {
      const typeMap = {
        FEEDING: "投喂",
        HEALTH: "健康",
        ENVIRONMENT: "环境",
        WEIGHT: "体重",
        FECES: "排便"
      }
      return typeMap[type] || type || "-"
    },
    careRecordSummary(record) {
      const parts = []
      if (record.foodName) {
        parts.push(record.foodName)
      }
      if (record.feedingCount || record.feedingUnit) {
        parts.push(`${record.feedingCount || ""}${record.feedingUnit || ""}`)
      }
      if (record.weightValue) {
        parts.push(this.formatWeight(record.weightValue))
      }
      if (record.temperature) {
        parts.push(`${record.temperature}°C`)
      }
      if (record.humidity) {
        parts.push(`${record.humidity}%`)
      }
      if (record.moodStatus) {
        parts.push(record.moodStatus)
      }
      if (record.isRefused === 1) {
        parts.push("拒食")
      }
      return parts.length > 0 ? parts.join(" / ") : "-"
    },
    async copyOpenId(user) {
      if (!user || !user.openId) {
        return
      }
      await this.copyText(user.openId)
      this.copiedUserId = user.id
      setTimeout(() => {
        if (this.copiedUserId === user.id) {
          this.copiedUserId = null
        }
      }, 1400)
    },
    async copyText(text) {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text)
        return
      }
      const input = document.createElement("textarea")
      input.value = text
      input.setAttribute("readonly", "readonly")
      input.style.position = "fixed"
      input.style.opacity = "0"
      document.body.appendChild(input)
      input.select()
      document.execCommand("copy")
      document.body.removeChild(input)
    },
    handleKeywordChange() {
      if (this.keywordTimer) {
        clearTimeout(this.keywordTimer)
      }
      this.keywordTimer = setTimeout(() => {
        this.currentPage = 1
        this.loadUsers()
      }, 240)
    },
    handlePageSizeChange() {
      this.currentPage = 1
      this.loadUsers()
    },
    goToPage(page) {
      const nextPage = Math.min(Math.max(page, 1), this.totalPages)
      if (nextPage === this.currentPage) {
        return
      }
      this.currentPage = nextPage
      this.loadUsers()
    },
    ensureCurrentPageInRange() {
      if (this.currentPage > this.totalPages) {
        this.currentPage = this.totalPages
      }
      if (this.currentPage < 1) {
        this.currentPage = 1
      }
    }
  }
}
</script>

<style scoped>
.user-list-page {
  min-height: calc(100vh - 150px);
}

.user-list-main {
  min-height: 0;
  height: calc(100vh - 150px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.user-list-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.user-list-search {
  flex: 1;
  max-width: 380px;
}

.user-list-search input {
  width: 100%;
  height: 40px;
  padding: 0 14px;
  border: 1px solid #d7deea;
  border-radius: 12px;
  background: #ffffff;
  color: #111827;
}

.user-list-toolbar__meta {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  color: #64748b;
  font-size: 13px;
}

.user-list-page-size {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.user-list-page-size select {
  height: 34px;
  padding: 0 10px;
  border: 1px solid #d7deea;
  border-radius: 10px;
  background: #ffffff;
}

.user-list-state {
  margin-bottom: 16px;
}

.user-list-table-wrap,
.user-pet-modal__table-wrap {
  min-height: 0;
  flex: 1;
  overflow: auto;
  border: 1px solid #e8edf3;
  border-radius: 16px;
  background: #ffffff;
}

.user-list-table {
  min-width: 1360px;
}

.user-pet-modal__table-wrap .table {
  min-width: 778px;
}

.user-list-table th:nth-child(1),
.user-list-table td:nth-child(1) {
  width: 72px;
}

.user-list-table th:nth-child(2),
.user-list-table td:nth-child(2) {
  width: 220px;
}

.user-list-table th:nth-child(3),
.user-list-table td:nth-child(3) {
  width: 260px;
}

.user-list-table th:nth-child(4),
.user-list-table td:nth-child(4) {
  width: 140px;
}

.user-list-table th:nth-child(5),
.user-list-table td:nth-child(5) {
  width: 100px;
}

.user-list-table th:nth-child(6),
.user-list-table td:nth-child(6) {
  width: 88px;
}

.user-list-table th:nth-child(7),
.user-list-table td:nth-child(7) {
  width: 180px;
}

.user-list-table th:nth-child(8),
.user-list-table td:nth-child(8) {
  width: 220px;
}

.user-pet-modal__table-wrap th:nth-child(1),
.user-pet-modal__table-wrap td:nth-child(1) {
  width: 72px;
}

.user-pet-modal__table-wrap th:nth-child(2),
.user-pet-modal__table-wrap td:nth-child(2) {
  width: 120px;
}

.user-pet-modal__table-wrap th:nth-child(3),
.user-pet-modal__table-wrap td:nth-child(3) {
  width: 150px;
}

.user-pet-modal__table-wrap th:nth-child(4),
.user-pet-modal__table-wrap td:nth-child(4) {
  width: 100px;
}

.user-pet-modal__table-wrap th:nth-child(5),
.user-pet-modal__table-wrap td:nth-child(5) {
  width: 140px;
}

.user-pet-modal__table-wrap th:nth-child(6),
.user-pet-modal__table-wrap td:nth-child(6) {
  width: 196px;
}

.user-list-table thead th,
.user-pet-modal__table-wrap thead th {
  position: sticky;
  top: 0;
  z-index: 1;
}

.user-list-table .sticky-action-column,
.user-pet-modal__table-wrap .sticky-action-column {
  position: sticky;
  right: 0;
  width: 400px;
  min-width: 400px;
  max-width: 400px;
  z-index: 3;
  background: #ffffff;
  box-shadow: -10px 0 18px rgba(15, 23, 42, 0.08);
  white-space: nowrap;
}

.user-pet-modal__table-wrap .sticky-action-column {
  width: 196px;
  min-width: 196px;
  max-width: 196px;
}

.user-list-table thead .sticky-action-column,
.user-pet-modal__table-wrap thead .sticky-action-column {
  z-index: 5;
  background: #f8fafc;
}

.user-cell {
  display: flex;
  align-items: center;
  min-width: 180px;
}

.user-avatar {
  width: 38px;
  height: 38px;
  margin-right: 10px;
  flex-shrink: 0;
  border-radius: 50%;
  overflow: hidden;
  background: #eef4ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
}

.user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-cell__copy {
  min-width: 0;
}

.user-cell__name {
  color: #111827;
  font-weight: 700;
  line-height: 20px;
}

.user-cell__id {
  margin-top: 2px;
  color: #94a3b8;
  font-size: 12px;
  line-height: 16px;
}

.user-list-openid {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-list-actions {
  width: 380px;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: nowrap;
}

.pet-row-actions {
  width: 172px;
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: nowrap;
}

.pet-row-actions .table-action-button {
  height: 30px;
  padding: 0 3px;
  border-radius: 9px;
}

.table-action-button {
  height: 32px;
  padding: 0 10px;
  border: 1px solid #2563eb;
  border-radius: 10px;
  background: #2563eb;
  color: #ffffff;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
}

.table-action-button--ghost {
  border-color: #d7deea;
  background: #ffffff;
  color: #334155;
}

.table-action-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.user-list-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 16px;
}

.user-list-footer__meta {
  color: #64748b;
  font-size: 13px;
}

.user-list-pagination {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.user-list-page-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 34px;
  height: 34px;
  padding: 0 10px;
  border: 1px solid #d7deea;
  border-radius: 10px;
  background: #ffffff;
  color: #334155;
  cursor: pointer;
}

.user-list-page-button--active {
  border-color: #2563eb;
  background: #2563eb;
  color: #ffffff;
}

.user-pet-modal {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.36);
}

.pet-detail-modal {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.42);
}

.user-pet-modal__panel {
  width: min(780px, 100%);
  max-height: min(720px, calc(100vh - 48px));
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 18px;
  background: #ffffff;
  border: 1px solid #e8edf3;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.22);
}

.pet-detail-modal__panel {
  width: min(920px, 100%);
  max-height: min(760px, calc(100vh - 48px));
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 18px;
  background: #ffffff;
  border: 1px solid #e8edf3;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.24);
}

.user-pet-modal__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 20px 14px;
  border-bottom: 1px solid #eef2f7;
}

.modal-close-button {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border: 1px solid #d7deea;
  border-radius: 10px;
  background: #ffffff;
  color: #475569;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
}

.user-pet-modal__state {
  padding: 20px;
}

.user-pet-modal__table-wrap {
  margin: 16px 20px 0;
}

.user-pet-modal__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px 20px;
  color: #64748b;
  font-size: 13px;
}

.pet-detail-body {
  min-height: 0;
  flex: 1;
  overflow: auto;
  padding: 18px 20px 0;
}

.pet-profile-detail {
  display: grid;
  gap: 16px;
}

.pet-cover-preview {
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 16px;
  background: #f8fafc;
  border: 1px solid #e8edf3;
  color: #94a3b8;
}

.pet-cover-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.pet-profile-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px;
  border: 1px solid #e8edf3;
  border-radius: 16px;
  background: #ffffff;
}

.pet-avatar-preview {
  width: 74px;
  height: 74px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 18px;
  background: #eef4ff;
  color: #2563eb;
  font-size: 22px;
  font-weight: 800;
}

.pet-avatar-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.pet-profile-card__copy {
  min-width: 0;
}

.pet-profile-card__name {
  color: #111827;
  font-size: 18px;
  font-weight: 800;
}

.pet-profile-card__meta {
  margin-top: 4px;
  color: #64748b;
  font-size: 13px;
}

.pet-profile-card__intro {
  margin-top: 8px;
  color: #475569;
  font-size: 13px;
  line-height: 20px;
}

.pet-detail-grid,
.pet-cost-summary {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.pet-detail-field {
  min-width: 0;
  padding: 12px;
  border: 1px solid #e8edf3;
  border-radius: 12px;
  background: #f8fafc;
}

.pet-detail-field span {
  display: block;
  color: #64748b;
  font-size: 12px;
  line-height: 16px;
}

.pet-detail-field strong {
  display: block;
  margin-top: 6px;
  color: #111827;
  font-size: 14px;
  line-height: 20px;
  word-break: break-all;
}

.pet-detail-section {
  min-height: 0;
}

.pet-detail-table {
  min-width: 780px;
}

.pet-plan-block {
  margin-top: 14px;
  padding: 14px;
  border: 1px solid #e8edf3;
  border-radius: 12px;
  background: #ffffff;
}

.pet-plan-block h4 {
  margin: 0 0 8px;
  color: #111827;
  font-size: 15px;
}

.pet-plan-block p {
  margin: 0;
  color: #475569;
  font-size: 13px;
  line-height: 20px;
  white-space: pre-wrap;
}

.user-pet-modal__foot-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.pet-create-modal {
  position: fixed;
  inset: 0;
  z-index: 120;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.46);
}

.pet-create-modal__panel {
  width: min(1080px, 100%);
  max-height: min(860px, calc(100vh - 48px));
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 18px;
  background: #ffffff;
  border: 1px solid #e8edf3;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.26);
}

.pet-create-tabs {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px 0;
}

.pet-create-tab {
  height: 36px;
  padding: 0 16px;
  border: 1px solid #d7deea;
  border-radius: 12px;
  background: #ffffff;
  color: #334155;
  font-size: 13px;
  cursor: pointer;
}

.pet-create-tab--active {
  border-color: #2563eb;
  background: #eef4ff;
  color: #2563eb;
  font-weight: 700;
}

.pet-create-body {
  min-height: 0;
  flex: 1;
  overflow: auto;
  padding: 16px 20px 0;
}

.pet-create-notice {
  margin-bottom: 14px;
  padding: 10px 14px;
  border: 1px solid #bfdbfe;
  border-radius: 12px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 13px;
  line-height: 20px;
}

.pet-create-section {
  padding-bottom: 8px;
}

.pet-form-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.pet-form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.pet-form-field--full {
  margin-top: 12px;
}

.pet-form-field > span {
  color: #64748b;
  font-size: 12px;
  line-height: 16px;
}

.pet-form-field input,
.pet-form-field select,
.pet-form-field textarea {
  width: 100%;
  min-width: 0;
  padding: 0 12px;
  border: 1px solid #d7deea;
  border-radius: 10px;
  background: #ffffff;
  color: #111827;
  font-size: 13px;
}

.pet-form-field input,
.pet-form-field select {
  height: 38px;
}

.pet-form-field textarea {
  padding: 10px 12px;
  line-height: 20px;
  resize: vertical;
}

.pet-form-field select:disabled {
  background: #f8fafc;
  color: #94a3b8;
}

.pet-form-media-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 16px;
}

.pet-form-media {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px;
  border: 1px solid #e8edf3;
  border-radius: 14px;
  background: #f8fafc;
}

.pet-form-media__preview {
  width: 132px;
  height: 84px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #e8edf3;
  color: #94a3b8;
  font-size: 12px;
}

.pet-form-media__preview--avatar {
  width: 84px;
}

.pet-form-media__preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.pet-form-media__copy {
  min-width: 0;
}

.pet-form-media__title {
  color: #111827;
  font-size: 13px;
  font-weight: 700;
}

.pet-form-media__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  flex-wrap: wrap;
}

.pet-form-media__actions .ghost-button {
  height: 32px;
  padding: 0 12px;
  border-radius: 10px;
  font-size: 12px;
}

.pet-form-media__input {
  display: none;
}

.pet-ai-upload {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.pet-ai-dropzone {
  position: relative;
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px dashed #c7d2e2;
  border-radius: 16px;
  background: #f8fafc;
  color: #64748b;
  font-size: 13px;
  cursor: pointer;
}

.pet-ai-dropzone--filled {
  border-style: solid;
  background: #ffffff;
}

.pet-ai-dropzone img {
  max-width: 100%;
  max-height: 320px;
  object-fit: contain;
}

.pet-ai-dropzone__hint {
  max-width: 560px;
  padding: 24px;
  text-align: center;
  line-height: 20px;
}

.pet-ai-dropzone__hint strong {
  display: block;
  margin-bottom: 8px;
  color: #111827;
  font-size: 15px;
}

.pet-ai-upload__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.pet-ai-message {
  margin-top: 14px;
  padding: 10px 14px;
  border: 1px solid #bfdbfe;
  border-radius: 12px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 13px;
  line-height: 20px;
}

.pet-ai-message--error {
  border-color: #fecaca;
  background: #fef2f2;
  color: #b91c1c;
}

.pet-ai-empty {
  margin-top: 16px;
}

.pet-ai-toolbar {
  display: flex;
  align-items: center;
  gap: 18px;
  margin-top: 16px;
  padding: 10px 14px;
  border: 1px solid #e8edf3;
  border-radius: 12px;
  background: #f8fafc;
  color: #475569;
  font-size: 13px;
  flex-wrap: wrap;
}

.pet-ai-toolbar__check {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.pet-ai-toolbar__count {
  margin-left: auto;
  color: #64748b;
}

.pet-ai-list {
  display: grid;
  gap: 14px;
  margin-top: 14px;
}

.pet-ai-item {
  padding: 14px;
  border: 1px solid #e8edf3;
  border-radius: 16px;
  background: #ffffff;
}

.pet-ai-item__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.pet-ai-item__check {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.pet-ai-item__title {
  color: #111827;
  font-size: 14px;
  font-weight: 700;
}

.pet-ai-item__badge {
  flex-shrink: 0;
  padding: 3px 10px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #475569;
  font-size: 12px;
}

.pet-ai-item__badge--high {
  background: #ecfdf5;
  color: #047857;
}

.pet-ai-item__badge--medium {
  background: #fffbeb;
  color: #b45309;
}

.pet-ai-item__badge--low {
  background: #fef2f2;
  color: #b91c1c;
}

.pet-ai-item__grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-top: 14px;
}

.pet-ai-item__note {
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #f8fafc;
  color: #475569;
  font-size: 13px;
  line-height: 20px;
}

.pet-ai-item__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 12px;
}

.pet-ai-item__state {
  color: #16a34a;
  font-size: 12px;
}

.pet-create-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px 20px;
  border-top: 1px solid #eef2f7;
}

.pet-create-foot__hint {
  color: #64748b;
  font-size: 13px;
  line-height: 20px;
}

.pet-create-foot__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.user-list-head-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.user-list-filters {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}

.user-list-filter {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #64748b;
  font-size: 13px;
}

.user-list-filter select {
  height: 40px;
  padding: 0 10px;
  border: 1px solid #d7deea;
  border-radius: 12px;
  background: #ffffff;
  color: #111827;
}

.user-list-filter--switch {
  padding: 0 12px;
  height: 40px;
  border: 1px solid #d7deea;
  border-radius: 12px;
  background: #ffffff;
  cursor: pointer;
}

.user-list-filter--switch input {
  width: 15px;
  height: 15px;
}

.user-cell__badge {
  display: inline-flex;
  align-items: center;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 999px;
  background: #eef4ff;
  color: #2563eb;
  font-size: 11px;
}

.user-openid-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mini-button {
  flex-shrink: 0;
  height: 26px;
  padding: 0 10px;
  border: 1px solid #d7deea;
  border-radius: 9px;
  background: #ffffff;
  color: #475569;
  font-size: 12px;
  cursor: pointer;
}

.mini-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.user-status-tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  white-space: nowrap;
}

.user-status-tag--normal {
  background: #ecfdf5;
  color: #047857;
}

.user-status-tag--disabled {
  background: #fff7ed;
  color: #b45309;
}

.user-status-tag--deleted {
  background: #f1f5f9;
  color: #64748b;
}

.table-action-button--danger {
  border-color: #fecaca;
  background: #ffffff;
  color: #b91c1c;
}

.table-action-button--danger:hover {
  background: #fef2f2;
}

.danger-action-button {
  height: 40px;
  padding: 0 18px;
  border: 0;
  border-radius: 12px;
  background: #dc2626;
  color: #ffffff;
  font-weight: 700;
  cursor: pointer;
}

.danger-action-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.user-editor-panel {
  width: min(660px, 100%);
  max-height: min(760px, calc(100vh - 48px));
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 18px;
  background: #ffffff;
  border: 1px solid #e8edf3;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.26);
}

.user-editor-body {
  min-height: 0;
  flex: 1;
  overflow: auto;
  padding: 18px 20px 0;
}

.user-editor-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.user-editor-avatar {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 16px;
  padding: 14px;
  border: 1px solid #e8edf3;
  border-radius: 12px;
  background: #fbfcfe;
}

.user-editor-avatar__preview {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  border-radius: 14px;
  border: 1px dashed #d7deea;
  background: #ffffff;
  color: #9aa3af;
  font-size: 22px;
  font-weight: 600;
  overflow: hidden;
  flex-shrink: 0;
}

.user-editor-avatar__preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-editor-avatar__body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.user-avatar-upload-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: flex-start;
  height: 34px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px dashed #d9c09a;
  background: #fffaf2;
  color: #a1620a;
  font-size: 13px;
  cursor: pointer;
}

.user-avatar-upload-button input {
  display: none;
}

.user-avatar-input {
  height: 36px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #dfe3e8;
  font-size: 13px;
  color: #1f2937;
  box-sizing: border-box;
}

.user-editor-avatar__hint {
  font-size: 12px;
  color: #9ca3af;
}

.user-editor-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-top: 16px;
  padding: 12px 14px;
  border: 1px solid #e8edf3;
  border-radius: 12px;
  background: #f8fafc;
  color: #64748b;
  font-size: 12px;
  line-height: 18px;
}

.user-confirm-panel {
  width: min(520px, 100%);
  max-height: min(680px, calc(100vh - 48px));
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 18px;
  background: #ffffff;
  border: 1px solid #e8edf3;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.26);
}

.user-confirm-body {
  min-height: 0;
  flex: 1;
  overflow: auto;
  padding: 18px 20px 0;
}

.user-confirm-list {
  margin: 0;
  padding-left: 18px;
  color: #475569;
  font-size: 13px;
  line-height: 22px;
}

.user-confirm-check {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 16px;
  padding: 12px 14px;
  border: 1px solid #fecaca;
  border-radius: 12px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 13px;
  line-height: 20px;
  cursor: pointer;
}

.user-confirm-check input {
  margin-top: 3px;
}

.user-toast {
  position: fixed;
  right: 28px;
  bottom: 28px;
  z-index: 200;
  padding: 12px 18px;
  border-radius: 12px;
  background: #0f172a;
  color: #ffffff;
  font-size: 13px;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.28);
}

.user-toast--error {
  background: #b91c1c;
}

@media (max-width: 1360px) {
  .user-list-main {
    height: auto;
    min-height: 0;
  }

  .user-list-table-wrap {
    max-height: 520px;
  }
}

@media (max-width: 720px) {
  .user-list-toolbar,
  .user-list-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .user-list-head-actions {
    width: 100%;
    justify-content: flex-end;
  }

  .user-list-search {
    max-width: none;
  }

  .user-editor-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .user-list-pagination {
    justify-content: flex-start;
  }

  .user-pet-modal {
    padding: 12px;
  }

  .pet-detail-modal {
    padding: 12px;
  }

  .pet-detail-grid,
  .pet-cost-summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .pet-cover-preview {
    height: 140px;
  }

  .pet-create-modal {
    padding: 12px;
  }

  .pet-form-grid,
  .pet-ai-item__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .pet-form-media-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .pet-create-foot {
    align-items: stretch;
    flex-direction: column;
  }

  .pet-create-foot__actions {
    justify-content: flex-end;
  }
}

/* ===== 用户「我的」界面（移动端还原预览） ===== */
.mine-preview-modal {
  position: fixed;
  inset: 0;
  z-index: 140;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.5);
}

.mine-preview-panel {
  width: min(1000px, 100%);
  max-height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 18px;
  background: #ffffff;
  border: 1px solid #e8edf3;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.28);
}

.mine-preview-state {
  padding: 28px;
}

.mine-preview-body {
  min-height: 0;
  flex: 1;
  overflow: auto;
  display: flex;
  gap: 24px;
  padding: 20px;
  background: #f8fafc;
}

.mine-phone {
  flex-shrink: 0;
  width: 375px;
  height: 720px;
  margin: 0 auto;
  padding: 10px;
  box-sizing: content-box;
  border-radius: 46px;
  background: #0f172a;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.25);
}

.mine-phone-screen {
  position: relative;
  width: 375px;
  height: 720px;
  overflow-y: auto;
  border-radius: 38px;
  background: #fafafa;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}

.mine-hero {
  position: relative;
  height: 230px;
  overflow: hidden;
}

.mine-hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.mine-hero-mask {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.16);
}

.mine-profile-card {
  position: relative;
  z-index: 3;
  margin: -50px 12px 0;
  padding: 13px 14px;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #f0f0f0;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.04);
}

.mine-profile-top {
  display: flex;
  align-items: center;
}

.mine-avatar {
  width: 46px;
  height: 46px;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 50%;
  background: #f1f5f9;
  color: #6b7280;
  font-size: 18px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mine-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.mine-profile-main {
  flex: 1;
  min-width: 0;
  margin-left: 11px;
}

.mine-profile-name {
  font-size: 18px;
  line-height: 26px;
  color: #171717;
  font-weight: 800;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mine-profile-subtitle {
  margin-top: 3px;
  font-size: 11px;
  line-height: 15px;
  color: #9a9a9a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mine-profile-arrow {
  margin-left: 8px;
  font-size: 22px;
  line-height: 24px;
  color: #4a4a4a;
}

.mine-stat-row {
  display: flex;
  margin-top: 9px;
  padding-top: 8px;
  border-top: 1px solid #f1f1f1;
}

.mine-stat {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.mine-stat-value {
  font-size: 15px;
  line-height: 19px;
  color: #171717;
  font-weight: 800;
}

.mine-stat-label {
  margin-top: 1px;
  font-size: 10px;
  line-height: 14px;
  color: #9a9a9a;
}

.mine-official-tag {
  display: inline-block;
  margin-top: 8px;
  padding: 2px 8px;
  border-radius: 8px;
  background: #eef4ff;
  color: #2563eb;
  font-size: 10px;
  font-weight: 700;
}

.mine-section {
  margin: 14px 12px 0;
}

.mine-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.mine-section-title {
  font-size: 16px;
  color: #161616;
  font-weight: 700;
}

.mine-section-meta {
  font-size: 11px;
  color: #9a9a9a;
}

.mine-pet-row {
  display: flex;
  gap: 10px;
  margin-top: 10px;
  overflow-x: auto;
  padding-bottom: 4px;
}

.mine-pet-card {
  flex: 0 0 auto;
  width: 118px;
  display: flex;
  align-items: center;
  padding: 9px 10px;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid #f0f0f0;
}

.mine-pet-avatar {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border-radius: 10px;
  object-fit: cover;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mine-pet-avatar--fallback {
  background: #eef4ff;
  color: #2563eb;
  font-size: 15px;
  font-weight: 800;
}

.mine-pet-copy {
  flex: 1;
  min-width: 0;
  margin-left: 9px;
}

.mine-pet-name {
  display: block;
  font-size: 13px;
  line-height: 16px;
  color: #111827;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mine-pet-species {
  display: block;
  margin-top: 4px;
  font-size: 10px;
  line-height: 14px;
  color: #94a3b8;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mine-pet-empty {
  margin-top: 10px;
  padding: 16px;
  border-radius: 14px;
  background: #ffffff;
  border: 1px dashed #dbe2ea;
  display: flex;
  align-items: center;
  gap: 10px;
}

.mine-pet-empty-plus {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #f1f5f9;
  color: #94a3b8;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mine-pet-empty-text {
  font-size: 12px;
  color: #94a3b8;
}

.mine-quick-panel {
  display: flex;
  margin-top: 10px;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #f0f0f0;
  overflow: hidden;
}

.mine-quick-card {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 6px;
}

.mine-quick-card + .mine-quick-card {
  border-left: 1px solid #f1f1f1;
}

.mine-quick-icon {
  width: 26px;
  height: 26px;
  object-fit: contain;
}

.mine-quick-title {
  font-size: 13px;
  color: #1f2937;
  font-weight: 600;
}

.mine-menu-card {
  margin-top: 10px;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #f0f0f0;
  overflow: hidden;
}

.mine-menu-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-bottom: 1px solid #f4f4f4;
}

.mine-menu-row--last {
  border-bottom-width: 0;
}

.mine-menu-icon {
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mine-menu-icon img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.mine-menu-icon--danger {
  border-radius: 50%;
  background: #fef2f2;
  color: #dc2626;
  font-size: 14px;
  font-weight: 800;
}

.mine-menu-main {
  flex: 1;
  min-width: 0;
}

.mine-menu-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  line-height: 18px;
  color: #1f2937;
  font-weight: 600;
}

.mine-menu-title--danger {
  color: #dc2626;
}

.mine-menu-badge {
  padding: 0 6px;
  border-radius: 8px;
  background: #ef4444;
  color: #ffffff;
  font-size: 10px;
  font-style: normal;
}

.mine-menu-desc {
  display: block;
  margin-top: 2px;
  font-size: 11px;
  line-height: 15px;
  color: #9ca3af;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mine-menu-arrow {
  color: #cbd5e1;
  font-size: 18px;
  line-height: 18px;
}

.mine-menu-arrow--danger {
  color: #fca5a5;
}

.mine-version-card {
  margin: 16px 12px 24px;
  padding: 14px;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.mine-version-title {
  font-size: 14px;
  color: #1f2937;
  font-weight: 700;
}

.mine-version-desc {
  margin-top: 3px;
  font-size: 11px;
  color: #9ca3af;
}

.mine-version-badge {
  padding: 4px 10px;
  border-radius: 10px;
  background: #ecfdf5;
  color: #059669;
  font-size: 11px;
  font-weight: 700;
}

.mine-preview-side {
  flex: 1;
  min-width: 260px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.mine-preview-card {
  padding: 16px;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid #e8edf3;
}

.mine-preview-card__title {
  color: #111827;
  font-size: 14px;
  font-weight: 800;
  margin-bottom: 10px;
}

.mine-preview-field {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 7px 0;
  border-bottom: 1px dashed #eef2f7;
}

.mine-preview-field:last-child {
  border-bottom-width: 0;
}

.mine-preview-field span {
  color: #64748b;
  font-size: 12px;
  flex-shrink: 0;
}

.mine-preview-field strong {
  color: #111827;
  font-size: 13px;
  word-break: break-all;
  text-align: right;
}

.mine-preview-openid {
  font-size: 12px !important;
  font-weight: 600;
}

.mine-preview-metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.mine-preview-metric {
  padding: 10px;
  border-radius: 12px;
  background: #f8fafc;
  text-align: center;
}

.mine-preview-metric strong {
  display: block;
  color: #111827;
  font-size: 18px;
}

.mine-preview-metric span {
  display: block;
  margin-top: 2px;
  color: #94a3b8;
  font-size: 11px;
}

.mine-preview-note {
  padding: 12px 14px;
  border-radius: 12px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1d4ed8;
  font-size: 12px;
  line-height: 20px;
}

@media (max-width: 980px) {
  .mine-preview-body {
    flex-direction: column;
  }

  .mine-phone {
    margin: 0 auto;
  }
}
</style>
